import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, FormGroup, FormLabel, FormControl, Pagination, InputGroup, Row, Col, Container, Nav, Modal, Table, Card } from 'react-bootstrap';
import '../DevDashboard.css';
import { isAdmin, hasPermission, isDeveloperOrAdmin } from '../utils/permissions';
import axios from 'axios';
import Cookies from 'js-cookie';
import { showToast as toast } from '../toastManager';
import analyzeCustomEventScript from '../utils/analyzeCustomEventScript';
import { useQueue } from '../context/QueueContext';
import AICampaignCreation from './AICampaignCreation';

// Helper function to ensure file_url always has cache-buster parameter
const ensureCacheBuster = (url) => {
  if (!url || typeof url !== 'string') return url;
  
  // Always update/replace the cache-buster parameter with current timestamp
  // This ensures fresh content is loaded even if URL already had a cache-buster
  // Remove existing v= parameter if present, then add new one
  const urlWithoutCacheBuster = url.replace(/[?&]v=\d+/g, '').replace(/\?$/, '');
  const newSeparator = urlWithoutCacheBuster.includes('?') ? '&' : '?';
  
  return `${urlWithoutCacheBuster}${newSeparator}v=${Date.now()}`;
};

// Imported Section HTML checklist: returns { errors: [], warnings: [] }
const analyzeImportedSectionHtml = (htmlContent, formData = {}) => {
  const errors = [];
  const warnings = [];
  if (!htmlContent || typeof htmlContent !== 'string') return { errors, warnings };

  const sxiPrefixPattern = /^sxi[_a-zA-Z0-9]*_?/i; // sxi or sxi_xxx_ style

  // 1) Classes/IDs/JS must use sxi prefix and be consistent
  const classRegex = /class\s*=\s*["']([^"']+)["']/gi;
  const idRegex = /id\s*=\s*["']([^"']+)["']/gi;
  const classInCssRegex = /\.([a-zA-Z_][a-zA-Z0-9_-]*)(?=\s*[{,])/g;
  const idInCssRegex = /#([a-zA-Z_][a-zA-Z0-9_-]*)(?=\s*[{,])/g;
  const tokensWithoutSxi = new Set();
  let m;
  while ((m = classRegex.exec(htmlContent)) !== null) {
    m[1].split(/\s+/).forEach((c) => { if (c && !sxiPrefixPattern.test(c)) tokensWithoutSxi.add('class:' + c); });
  }
  while ((m = idRegex.exec(htmlContent)) !== null) {
    m[1].split(/\s+/).forEach((id) => { if (id && !sxiPrefixPattern.test(id)) tokensWithoutSxi.add('id:' + id); });
  }
  const styleBlock = htmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
  if (styleBlock) {
    styleBlock.forEach((block) => {
      const inner = block.replace(/<style[^>]*>|<\/style>/gi, '');
      while ((m = classInCssRegex.exec(inner)) !== null) {
        if (!sxiPrefixPattern.test(m[1])) tokensWithoutSxi.add('css class:.' + m[1]);
      }
      while ((m = idInCssRegex.exec(inner)) !== null) {
        if (!sxiPrefixPattern.test(m[1])) tokensWithoutSxi.add('css id:#' + m[1]);
      }
    });
  }
  // Collect callback/arrow function parameter names to exempt from sxi prefix (e.g. forEach(item =>), (entry, observer) =>)
  const callbackParamNames = new Set();
  const scriptBlocks = htmlContent.match(/<script[^>]*>([\s\S]*?)<\/script>/gi);
  if (scriptBlocks) {
    const paramListRegex = /\(\s*([^)]*)\s*\)\s*=>/g;
    const singleParamRegex = /(?:\.\w+\s*\(\s*|\|\s*)(\w+)\s*=>/g; // .forEach(x => or | x =>
    const bareArrowParamRegex = /\b(\w+)\s*=>/g; // bare "video =>" (single param arrow)
    scriptBlocks.forEach((block) => {
      const inner = block.replace(/<script[^>]*>|<\/script>/gi, '');
      let pm;
      while ((pm = paramListRegex.exec(inner)) !== null) {
        pm[1].split(',').forEach((p) => { const name = p.trim().split(/\s*=/)[0].trim(); if (/^\w+$/.test(name)) callbackParamNames.add(name); });
      }
      while ((pm = singleParamRegex.exec(inner)) !== null) {
        if (/^\w+$/.test(pm[1])) callbackParamNames.add(pm[1]);
      }
      while ((pm = bareArrowParamRegex.exec(inner)) !== null) {
        if (/^\w+$/.test(pm[1])) callbackParamNames.add(pm[1]);
      }
    });
  }

  const funcRegex = /function\s+(\w+)\s*\(/g;
  const varRegex = /(?:var|let|const)\s+(\w+)\s*[=;]/g;
  while ((m = funcRegex.exec(htmlContent)) !== null) {
    if (!sxiPrefixPattern.test(m[1])) tokensWithoutSxi.add('function:' + m[1]);
  }
  while ((m = varRegex.exec(htmlContent)) !== null) {
    const varName = m[1];
    if (callbackParamNames.has(varName)) continue; // exempt callback parameters
    if (!sxiPrefixPattern.test(varName)) tokensWithoutSxi.add('variable:' + varName);
  }
  if (tokensWithoutSxi.size > 0) {
    errors.push({
      rule: 'sxi_prefix',
      message: 'All CSS classes, IDs, and JS variables/functions must use the prefix sxi_ or sxi_{unique_id}_',
      detail: Array.from(tokensWithoutSxi).slice(0, 15).join(', ') + (tokensWithoutSxi.size > 15 ? '...' : '')
    });
  }

  // 2) No inline style attributes
  if (/\sstyle\s*=\s*["'][^"']*["']/i.test(htmlContent)) {
    errors.push({
      rule: 'no_inline_styles',
      message: 'No HTML elements should include direct CSS in the style attribute. Remove inline styles.'
    });
  }

  // 3) No global tag selectors in <style> (body, a, h1-h6, div, span, p, etc.)
  const styleContent = htmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
  const globalTags = ['body', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'p', 'ul', 'ol', 'li', 'table', 'tr', 'td', 'th', 'img', 'input', 'button', 'form', 'label', 'header', 'footer', 'main', 'section', 'nav', 'article'];
  if (styleContent) {
    const foundGlobal = new Set();
    styleContent.forEach((block) => {
      const inner = block.replace(/<style[^>]*>|<\/style>/gi, '');
      const selectors = inner.split(/\{/).map((part) => part.replace(/[\s\S]*\}/g, '').trim()).filter(Boolean);
      selectors.forEach((sel) => {
        sel.split(',').forEach((s) => {
          const trimmed = s.trim().replace(/^\s*([^.#\[:]+).*/, '$1').trim().toLowerCase();
          if (globalTags.includes(trimmed)) foundGlobal.add(trimmed);
        });
      });
    });
    if (foundGlobal.size > 0) {
      errors.push({
        rule: 'no_global_tag_selectors',
        message: 'Do not use global tag selectors in <style> (e.g. body, a, h2). Use classes with sxi_ prefix.',
        detail: 'Found: ' + Array.from(foundGlobal).join(', ')
      });
    }
  }

  // 4) Global functions should use window
  const globalFuncRegex = /function\s+(\w+)\s*\(/g;
  const windowAssignRegex = /window\.\s*\w+\s*=/g;
  const funcNames = [];
  while ((m = globalFuncRegex.exec(htmlContent)) !== null) funcNames.push(m[1]);
  const hasWindowAssign = (windowAssignRegex.exec(htmlContent) !== null);
  windowAssignRegex.lastIndex = 0;
  if (funcNames.length > 0 && !hasWindowAssign) {
    warnings.push({
      rule: 'global_via_window',
      message: 'To declare JavaScript functions globally, use the window object (e.g. window.sxi_xxx_funcName = function ...).',
      detail: 'Consider assigning functions to window.'
    });
  }

  // 5) font-family should be removed (warning)
  if (/font-family\s*:/i.test(htmlContent)) {
    warnings.push({
      rule: 'remove_font_family',
      message: 'For the Imported Section, always remove font-family from CSS.'
    });
  }

  // 6) Section name: no special chars, must not start with number (when extension)
  if (formData.section_type === 'extension' && formData.section_name) {
    const name = String(formData.section_name).trim();
    if (/^\d/.test(name)) {
      errors.push({
        rule: 'section_name',
        message: "Section name must not start with a number when adding a name to the extension."
      });
    }
    if (!/^[a-zA-Z][a-zA-Z0-9_\s]*$/.test(name)) {
      errors.push({
        rule: 'section_name',
        message: 'Section name must not contain special characters (use only letters, numbers, underscore).'
      });
    }
  }

  return { errors, warnings };
};

const DevDashboard = ({ ViewedPage, app_url, shop_domain }) => {
  const [activeTab, setActiveTab] = useState('rearrange-section');
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Data states
  const [selectedStore, setSelectedStore] = useState('');
  const [landingPageUrls, setLandingPageUrls] = useState([]);
  const [selectedLandingPageUrl, setSelectedLandingPageUrl] = useState('');
  const [sectionsRearrangement, setSectionsRearrangement] = useState([]);
  const [approvedSections, setApprovedSections] = useState([]);
  const [importedSections, setImportedSections] = useState([]);
  const [lpSpreadsheetDetails, setLpSpreadsheetDetails] = useState(null);
  const [sectionsTags, setSectionsTags] = useState([]);
  
  // Form states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedHtmlFile, setSelectedHtmlFile] = useState(null);
  const [uploadingHtml, setUploadingHtml] = useState(false);
  const [htmlUploadedSuccessfully, setHtmlUploadedSuccessfully] = useState(false);
  const [validatingHtml, setValidatingHtml] = useState(false);
  const [htmlAnalysisReport, setHtmlAnalysisReport] = useState(null); // { fileName, errors: [], warnings: [] }
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [spreadsheetData, setSpreadsheetData] = useState({
    spreadsheetUrl: '',
    spreadsheetId: ''
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // AI Section JSON state
  const { addJob, jobs: queueJobs } = useQueue();
  const [aiSectionGenerating, setAiSectionGenerating] = useState(false);
  const [aiSectionGenerated, setAiSectionGenerated] = useState(false);
  const [aiSectionGeneratingOnServer, setAiSectionGeneratingOnServer] = useState(false);
  const [aiSectionJobId, setAiSectionJobId] = useState(null);
  const [importingSectionId, setImportingSectionId] = useState(null); // section_id of row being imported

  // Top-level page navigation: 'section' | 'ai-campaign-creation' | 'analytics'
  const [activePage, setActivePage] = useState('section');

  // Custom Events state
  const [customEventForm, setCustomEventForm] = useState({ landing_page: '', event_name: '', script: '' });
  const [customEvents, setCustomEvents] = useState([]);
  const [customEventsFilterLP, setCustomEventsFilterLP] = useState('');
  const [customEventLoading, setCustomEventLoading] = useState(false);
  const [customEventsListLoading, setCustomEventsListLoading] = useState(false);
  const [showEditCustomEventModal, setShowEditCustomEventModal] = useState(false);
  const [editingCustomEvent, setEditingCustomEvent] = useState(null);
  const [editCustomEventLoading, setEditCustomEventLoading] = useState(false);
  const [editCustomEventForm, setEditCustomEventForm] = useState({ event_name: '', script: '', is_active: true });
  const [scriptValidationReport, setScriptValidationReport] = useState(null);
  const [editScriptValidationReport, setEditScriptValidationReport] = useState(null);

  // Mapping from section IDs to page names in all_dev_page_mapping
  const sectionToPageMapping = {
    'rearrange-section': 'Rearrange Section',
    'approved-section': 'Approved Section',
    'imported-sections': 'Imported Sections',
    'custom-event': 'Custom Event',
    // 'spreadsheet': 'Spreadsheet Details'
  };

  // Helper function to normalize store key (remove .myshopify.com suffix)
  const normalizeStoreKey = (store) => {
    if (!store) return '';
    return store.replace('.myshopify.com', '');
  };

  // Dev sections with tab structure
  const allDevSections = [
    // { id: 'templates', title: 'Templates', component: 'templates' },
    { id: 'rearrange-section', title: 'Rearrange Section', component: 'rearrange-section' },
    { id: 'imported-sections', title: 'Imported Sections', component: 'imported-sections' },
    { id: 'approved-section', title: 'Approved Section', component: 'approved-section' },
    // { id: 'spreadsheet', title: 'Spreadsheet Details', component: 'spreadsheet' }
  ];

  // State to track current shop and permissions for permission checking
  const [currentShopForPermissions, setCurrentShopForPermissions] = useState('');
  const [devPagePermissionsByStoreState, setDevPagePermissionsByStoreState] = useState({});

  // Update current shop and permissions when shop_domain or cookies change
  useEffect(() => {
    const shopCookie = Cookies.get('shop') || '';
    const shopDomain = shop_domain || '';
    const shopKey = shopCookie || (shopDomain ? shopDomain.replace('.myshopify.com', '') : '');
    setCurrentShopForPermissions(shopKey);
    
    // Also update permissions state
    const devPagePermissionsByStoreStr = Cookies.get('dev_page_permissions_by_store');
    if (devPagePermissionsByStoreStr) {
      try {
        const permissions = JSON.parse(devPagePermissionsByStoreStr);
        // Normalize keys
        const normalized = {};
        Object.keys(permissions).forEach(key => {
          const normalizedKey = normalizeStoreKey(key);
          normalized[normalizedKey] = permissions[key];
        });
        setDevPagePermissionsByStoreState(normalized);
        console.log('DevDashboard: Loaded permissions', normalized, 'for shop', shopKey);
      } catch (error) {
        console.error('Error parsing dev_page_permissions_by_store:', error);
        setDevPagePermissionsByStoreState({});
      }
    } else {
      console.log('DevDashboard: No dev_page_permissions_by_store cookie found');
      setDevPagePermissionsByStoreState({});
    }
  }, [shop_domain]);

  // Filter sections based on permissions - memoized to prevent unnecessary recalculations
  const devSectionsResult = useMemo(() => {
    // If no shop, return empty result
    if (!currentShopForPermissions) {
      console.log('DevDashboard: No shop available');
      return { sections: [], hasCustomEvent: false };
    }
    
    // Get permissions for current store, fallback to dev_page_permissions if store-specific permissions don't exist
    let storePermissions = devPagePermissionsByStoreState[currentShopForPermissions] || [];
    
    // If store has no specific permissions, use global dev_page_permissions as fallback
    if (storePermissions.length === 0 && Object.keys(devPagePermissionsByStoreState).length > 0) {
      // Store exists in permissions object but has empty array - use fallback
      const devPagePermissionsStr = Cookies.get('dev_page_permissions');
      if (devPagePermissionsStr) {
        try {
          storePermissions = JSON.parse(devPagePermissionsStr) || [];
          console.log('DevDashboard: Using fallback dev_page_permissions:', storePermissions);
        } catch (error) {
          console.error('Error parsing dev_page_permissions:', error);
        }
      }
    } else if (storePermissions.length === 0 && Object.keys(devPagePermissionsByStoreState).length === 0) {
      // No store-specific permissions at all - try fallback
      const devPagePermissionsStr = Cookies.get('dev_page_permissions');
      if (devPagePermissionsStr) {
        try {
          storePermissions = JSON.parse(devPagePermissionsStr) || [];
          console.log('DevDashboard: Using fallback dev_page_permissions (no store-specific permissions):', storePermissions);
        } catch (error) {
          console.error('Error parsing dev_page_permissions:', error);
        }
      }
    }
    
    console.log('DevDashboard: Filtering sections for shop', currentShopForPermissions, 'with permissions:', storePermissions);
    console.log('DevDashboard: Available stores in permissions:', Object.keys(devPagePermissionsByStoreState));
    
    // Filter sections based on permissions
    const filtered = allDevSections.filter(section => {
      const pageName = sectionToPageMapping[section.id];
      const hasAccess = storePermissions.includes(pageName);
      console.log('DevDashboard: Section', section.id, '(', pageName, ') - Access:', hasAccess);
      return hasAccess;
    });
    
    console.log('DevDashboard: Filtered sections:', filtered.map(s => s.id));
    return {
      sections: filtered,
      hasCustomEvent: storePermissions.includes('Custom Event'),
      hasAiCampaignCreation: storePermissions.includes('AI Campaign Creation'),
    };
  }, [currentShopForPermissions, devPagePermissionsByStoreState]);

  const devSections = useMemo(() => devSectionsResult.sections, [devSectionsResult]);
  const hasCustomEventAccess = useMemo(() => devSectionsResult.hasCustomEvent, [devSectionsResult]);
  const hasAiCampaignCreationAccess = useMemo(() => devSectionsResult.hasAiCampaignCreation, [devSectionsResult]);

  useEffect(() => {
    ViewedPage(40);
    // Get shop name - prefer shop_name cookie (matches MongoDB) over normalized shop domain
    const shopName = Cookies.get('shop_name');
    const currentShop = shop_domain || Cookies.get('shop') || '';
    
    // Use shop_name cookie if available (matches MongoDB shop_name field), otherwise normalize shop domain
    const shopKey = shopName || (currentShop ? currentShop.replace('.myshopify.com', '') : '');
    
    if (shopKey) {
      setSelectedStore(shopKey);
      // Reset landing page URL when shop changes
      setSelectedLandingPageUrl('');
      setLandingPageUrls([]);
      setSectionsRearrangement([]);
      setApprovedSections([]);
      setAiSectionGenerated(false);
      setImportedSections([]);
      setLpSpreadsheetDetails(null);
      setSpreadsheetData({ spreadsheetUrl: '', spreadsheetId: '' });
      setCurrentPage(1);
      setSearchTerm('');
      fetchLandingPageUrls(shopKey);
    } else {
      setSelectedStore('');
      setLandingPageUrls([]);
      setSelectedLandingPageUrl('');
    }
  }, [ViewedPage, shop_domain]);

  useEffect(() => {
    if (app_url) {
      axios.get(`${app_url}/api/settings/sections-tags`, { withCredentials: true })
        .then(res => {
          if (res.data.success) setSectionsTags(res.data.data || []);
        })
        .catch(err => console.error('Error loading sections tags:', err));
    }
  }, [app_url]);

  // Ensure activeTab is valid (user has access to it)
  useEffect(() => {
    // Check if permissions have been loaded - if cookie exists, we've attempted to load
    const devPagePermissionsCookie = Cookies.get('dev_page_permissions_by_store');
    const permissionsLoaded = devPagePermissionsCookie !== undefined;
    
    if (devSections.length > 0) {
      // Clear any warning message when sections are available
      setMessage(prev => {
        if (prev.type === 'warning' && prev.text && prev.text.includes('do not have access')) {
          return { type: '', text: '' };
        }
        return prev;
      });
      
      const hasAccess = devSections.find(s => s.id === activeTab);
      if (!hasAccess) {
        // Current activeTab is not accessible, switch to first available section
        setActiveTab(devSections[0].id);
      }
    } else if (devSections.length === 0 && permissionsLoaded && currentShopForPermissions) {
      // Only show warning if:
      // 1. Permissions cookie exists (we've loaded permissions)
      // 2. We have a shop key
      // 3. But still no sections available
      // This means the user truly has no access, not that we're still loading
      const storePermissions = devPagePermissionsByStoreState[currentShopForPermissions];
      if (storePermissions !== undefined) {
        // Permissions for this store have been checked (even if empty array)
        setMessage({ type: 'warning', text: 'You do not have access to any Dev Panel sections for the current store.' });
      }
      // If storePermissions is undefined, we're still loading, so don't show warning
    }
  }, [devSections, activeTab, devPagePermissionsByStoreState, currentShopForPermissions]);

  // Get shop name from cookies or sidebar - prefer shop_name cookie (matches MongoDB)
  const getShopName = () => {
    const shopName = Cookies.get('shop_name');
    if (shopName) return shopName;
    
    const currentShop = shop_domain || Cookies.get('shop') || '';
    if (currentShop) {
      return currentShop.replace('.myshopify.com', '');
    }
    return 'Unknown Shop';
  };

  // Helper function to get shop for API calls - always prefer shop_name cookie (matches MongoDB)
  const getShopForApi = () => {
    return Cookies.get('shop_name') || selectedStore || '';
  };

  useEffect(() => {
    if (selectedStore) {
      if (activeTab === 'spreadsheet') {
        fetchSpreadsheetData();
      } else if (activeTab === 'imported-sections') {
        fetchImportedSections();
      } else if (selectedLandingPageUrl) {
        fetchStoreData();
      }
    }
  }, [selectedStore, selectedLandingPageUrl, activeTab]);

  // Watch for AI section generation job to complete, then refresh data
  useEffect(() => {
    if (!aiSectionJobId) return;
    const job = queueJobs.find(j => j.jobId === aiSectionJobId);
    if (!job) return;
    if (job.status === 'succeeded') {
      setAiSectionJobId(null);
      setAiSectionGenerating(false);
      fetchStoreData();
    } else if (job.status === 'failed') {
      setAiSectionJobId(null);
      setAiSectionGenerating(false);
      setAiSectionGeneratingOnServer(false);
    }
  }, [queueJobs, aiSectionJobId]);

  const fetchSpreadsheetData = async () => {
    if (!selectedStore) return;
    
    try {
      setLoading(true);
      // Use shop_name cookie if available (matches MongoDB), otherwise use selectedStore
      const shopToUse = Cookies.get('shop_name') || selectedStore;
      console.log('Fetching spreadsheet data for shop:', shopToUse);
      
      const response = await axios.get(
        app_url + `/api/dev/store-data/${shopToUse}/lp_spreadsheet_details`, 
        { withCredentials: true }
      );
      const data = response.data;
      console.log('Spreadsheet data response:', data);
      
      if (data.success) {
        // Handle both response structures:
        // 1. Direct: { success: true, data: { spreadsheetUrl, spreadsheetId } }
        // 2. Nested: { success: true, data: { lp_spreadsheet_details: { spreadsheetUrl, spreadsheetId }, ... } }
        const spreadsheetDetails = data.data?.lp_spreadsheet_details || data.data;
        
        console.log('Extracted spreadsheet details:', spreadsheetDetails);
        
        setLpSpreadsheetDetails(spreadsheetDetails);
        
        if (spreadsheetDetails && (spreadsheetDetails.spreadsheetUrl || spreadsheetDetails.spreadsheetId)) {
          setSpreadsheetData({
            spreadsheetUrl: spreadsheetDetails.spreadsheetUrl || '',
            spreadsheetId: spreadsheetDetails.spreadsheetId || ''
          });
        } else {
          setSpreadsheetData({
            spreadsheetUrl: '',
            spreadsheetId: ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching spreadsheet data:', error);
      setMessage({ type: 'error', text: 'Failed to fetch spreadsheet data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchImportedSections = async () => {
    if (!selectedStore) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        app_url + `/api/dev/imported-sections/${selectedStore}`, 
        { withCredentials: true }
      );
      const data = response.data;
      if (data.success) {
        setImportedSections(data.data?.sections || []);
      } else {
        setImportedSections([]);
      }
    } catch (error) {
      console.error('Error fetching imported sections:', error);
      setMessage({ type: 'error', text: 'Failed to fetch imported sections' });
      setImportedSections([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLandingPageUrls = async (shop) => {
    if (!shop) return;

    try {
      setFetchingData(true);
      // Use shop_name cookie if available (matches MongoDB), otherwise use provided shop
      const shopToUse = Cookies.get('shop_name') || shop;
      console.log('Fetching landing page URLs for shop:', shopToUse);

      const [lpResponse, domainResponse] = await Promise.allSettled([
        axios.get(app_url + `/api/dev/landing-page-urls/${shopToUse}`, { withCredentials: true }),
        axios.get(app_url + `/api/settings/domains-and-urls`, { withCredentials: true })
      ]);

      const lpUrls = lpResponse.status === 'fulfilled' && lpResponse.value.data.success
        ? (lpResponse.value.data.data || [])
        : [];
      const domainUrls = domainResponse.status === 'fulfilled' && domainResponse.value.data.success
        ? (domainResponse.value.data.data || [])
        : [];

      // Merge and deduplicate (case-insensitive)
      const seen = new Set(lpUrls.map(u => u.toLowerCase()));
      const merged = [...lpUrls, ...domainUrls.filter(u => !seen.has(u.toLowerCase()))];

      setLandingPageUrls(merged);
      if (merged.length > 0 && !selectedLandingPageUrl) {
        setSelectedLandingPageUrl(merged[0]);
      }
    } catch (error) {
      console.error('Error fetching landing page URLs:', error);
      setMessage({ type: 'error', text: 'Failed to fetch landing page URLs' });
    } finally {
      setFetchingData(false);
    }
  };

  const fetchStoreData = async () => {
    if (!selectedStore || !selectedLandingPageUrl) return;
    
    try {
      setLoading(true);
      // Use shop_name cookie if available (matches MongoDB), otherwise use selectedStore
      const shopToUse = Cookies.get('shop_name') || selectedStore;
      const response = await axios.post(
        app_url + `/api/dev/store-data`,
        {
          shop: shopToUse,
          landingPageUrl: selectedLandingPageUrl
        },
        { withCredentials: true }
      );
      const data = response.data;
      if (data.success) {
        setSectionsRearrangement(data.data.sections_rearrangement || []);
        setApprovedSections(data.data.approved_sections || []);
        setAiSectionGenerated(data.data.ai_section_json_generated || false);
        setAiSectionGeneratingOnServer(data.data.ai_section_json_generating || false);
        setLpSpreadsheetDetails(data.data.lp_spreadsheet_details || null);
        if (data.data.lp_spreadsheet_details) {
          setSpreadsheetData({
            spreadsheetUrl: data.data.lp_spreadsheet_details.spreadsheetUrl || '',
            spreadsheetId: data.data.lp_spreadsheet_details.spreadsheetId || ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching store data:', error);
      setMessage({ type: 'error', text: 'Failed to fetch store data' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setMessage({ type: '', text: '' });
    setCurrentPage(1);
    setSearchTerm('');
    if (selectedStore) {
      if (tabName === 'spreadsheet') {
        fetchSpreadsheetData();
      } else if (selectedLandingPageUrl) {
        fetchStoreData();
      }
    }
  };


  const handleLandingPageUrlChange = (e) => {
    setSelectedLandingPageUrl(e.target.value);
    setSectionsRearrangement([]);
    setApprovedSections([]);
    setLpSpreadsheetDetails(null);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const getFieldName = () => {
    switch (activeTab) {
      case 'rearrange-section':
        return 'sections_rearrangement';
      case 'approved-section':
        return 'approved_sections';
      default:
        return 'sections_rearrangement';
    }
  };

  const getCurrentItems = () => {
    switch (activeTab) {
      case 'rearrange-section':
        return sectionsRearrangement;
      case 'approved-section':
        return approvedSections;
      default:
        return [];
    }
  };

  const setCurrentItems = (items) => {
    switch (activeTab) {
      case 'rearrange-section':
        setSectionsRearrangement(items);
        break;
      case 'approved-section':
        setApprovedSections(items);
        break;
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type } = e.target;
    if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseFloat(value) : value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const resetForm = () => {
    if (activeTab === 'imported-sections') {
      setFormData({
        section_id: '',
        section_name: '',
        section_type: 'assets',
        rearrangement_type: 'dynamic',
        section_query_selector: '',
        section_original_url: '',
        file_url: ''
      });
      setSelectedHtmlFile(null);
      setHtmlUploadedSuccessfully(false);
      setHtmlAnalysisReport(null);
      setSelectedVersionId(null);
    } else {
      setFormData({
        section_id: '',
        section_name: '',
        section_description: '',
        section_query_selector: '',
        parent_query_selector: '',
        section_rearrangement: 'dynamic',
        image_url: '',
        version: 1,
        file_url: '',
        section_category: '',
        section_sub_category: ''
      });
    }
    setEditingItem(null);
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedVersionId(null);
    if (activeTab !== 'imported-sections') {
      setSelectedHtmlFile(null);
      setHtmlUploadedSuccessfully(false);
    }
  };

  const handleAdd = () => {
    resetForm();
    setEditingItem(null);
    setShowAddModal(true);
  };

  const handleAddRearrangeSection = () => {
    resetForm();
    setEditingItem(null);
    setShowEditModal(true);
  };

  const handleAiGenerateSections = async () => {
    if (!selectedStore || !selectedLandingPageUrl) return;
    let wasQueued = false;
    try {
      setAiSectionGenerating(true);
      const shopToUse = Cookies.get('shop_name') || selectedStore;
      const response = await axios.post(
        app_url + '/api/dev/store-data/generate-section-json',
        { shop: shopToUse, landingPageUrl: selectedLandingPageUrl },
        { withCredentials: true }
      );
      if (response.status === 202 && response.data?.jobId) {
        addJob(response.data.jobId, 'ai-sections', `AI Sections — ${selectedLandingPageUrl}`, selectedLandingPageUrl, app_url);
        setAiSectionJobId(response.data.jobId);
        wasQueued = true;
        return;
      }
      toast.error(response.data.message || 'AI generation failed');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error generating sections from AI');
    } finally {
      if (!wasQueued) setAiSectionGenerating(false);
    }
  };

  const handleImportSection = async (item) => {
    if (!selectedLandingPageUrl || !item.section_query_selector) {
      toast.error('Section query selector is required to import');
      return;
    }
    try {
      setImportingSectionId(item.section_id);
      const response = await axios.post(
        app_url + '/api/dev/store-data/import-section',
        {
          targetUrl: selectedLandingPageUrl,
          sectionName: item.section_name,
          querySelector: item.section_query_selector
        },
        { withCredentials: true }
      );
      if (response.data.success) {
        toast.success(`Section "${item.section_name}" imported successfully`);
      } else {
        toast.error(response.data.message || 'Import failed');
      }
    } catch (error) {
      console.error('Error importing section:', error);
      toast.error(error?.response?.data?.message || 'Error importing section');
    } finally {
      setImportingSectionId(null);
    }
  };

  const handleGenerateGrowthbookScript = async () => {
    if (!selectedStore) {
      setMessage({ type: 'error', text: 'Please select a store first' });
      return;
    }

    if (!selectedLandingPageUrl) {
      setMessage({ type: 'error', text: 'Please select a landing page URL first' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const response = await axios.post(
        `${app_url}/api/growthbook-experiments/create-template-script-gb`,
        {
          landingPageUrl: selectedLandingPageUrl,
          template: null // Can be set to a specific template if needed
        },
        { withCredentials: true }
      );

      if (response.data && response.data.success !== false) {
        setMessage({ 
          type: 'success', 
          text: response.data.message || '✅ Growthbook script generated successfully!' 
        });
      } else {
        setMessage({ 
          type: response.data.show_message ? 'error' : 'info', 
          text: response.data.message || 'Growthbook script generation completed' 
        });
      }
    } catch (error) {
      console.error('Error generating Growthbook script:', error);
      setMessage({ 
        type: 'error', 
        text: error?.response?.data?.message || 'Error generating Growthbook script. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item, index) => {
    setEditingItem({ ...item, index });
    setFormData({ ...item });
    setSelectedImage(null);
    // Use cache-buster to ensure updated image is shown when re-editing
    setImagePreview(item.image_url ? ensureCacheBuster(item.image_url) : null);
    setShowEditModal(true);
  };

  const handleEditImportedSection = (section, index) => {
    setEditingItem({ ...section, index });
    
    // Ensure versions array exists
    if (!section.versions || section.versions.length === 0) {
      console.warn('Section has no versions:', section);
      setSelectedVersionId(null);
      setFormData({
        section_id: section.section_id || '',
        section_name: section.section_name || '',
        section_type: section.section_type || 'assets',
        rearrangement_type: section.rearrangement_type || 'dynamic',
        section_query_selector: section.section_query_selector || '',
        section_original_url: section.section_original_url || '',
        file_url: ''
      });
      setSelectedHtmlFile(null);
      setHtmlAnalysisReport(null);
      setShowEditModal(true);
      return;
    }
    
    // Get live version data - use live_version fields from the version object
    const liveVersion = section.versions.find(v => v.version === section.live_version) || section.versions[0];
    // Set selected version to live version by default
    const defaultVersionId = liveVersion?.section_version_id || section.versions[0]?.section_version_id;
    setSelectedVersionId(defaultVersionId);
    
    // Load data from the selected version (default to live version)
    const selectedVersion = section.versions.find(v => v.section_version_id === defaultVersionId) || liveVersion || section.versions[0];
    setFormData({
      section_id: section.section_id || '',
      section_name: section.section_name || '',
      section_type: selectedVersion?.section_type || section.section_type || 'assets',
      rearrangement_type: section.rearrangement_type || 'dynamic',
      section_query_selector: selectedVersion?.section_query_selector || section.section_query_selector || '',
      section_original_url: selectedVersion?.section_original_url || section.section_original_url || '',
      file_url: selectedVersion?.file_url || ''
    });
    setSelectedHtmlFile(null);
    setHtmlAnalysisReport(null);
    setShowEditModal(true);
  };

  // Handle version selection change
  const handleVersionChange = (e) => {
    const versionId = e.target.value;
    setSelectedVersionId(versionId);
    setHtmlUploadedSuccessfully(false);
    
    if (editingItem && editingItem.versions) {
      // Find the selected version
      const selectedVersion = editingItem.versions.find(v => v.section_version_id === versionId);
      if (selectedVersion) {
        // Update form data with selected version's data
        setFormData(prev => ({
          ...prev,
          section_type: selectedVersion.section_type || prev.section_type,
          section_query_selector: selectedVersion.section_query_selector || prev.section_query_selector,
          section_original_url: selectedVersion.section_original_url || prev.section_original_url,
          file_url: selectedVersion.file_url || prev.file_url
        }));
      }
    }
  };

  const handleSetLiveVersion = async () => {
    if (!selectedVersionId || !editingItem) {
      setMessage({ type: 'error', text: 'Please select a version first' });
      return;
    }

    if (!selectedStore || !editingItem.section_id) {
      setMessage({ type: 'error', text: 'Store and Section ID are required' });
      return;
    }

    // Get the version number from the selected version
    const selectedVersion = editingItem.versions?.find(v => v.section_version_id === selectedVersionId);
    if (!selectedVersion) {
      setMessage({ type: 'error', text: 'Selected version not found' });
      return;
    }

    const targetVersion = selectedVersion.version;

    // Check if it's already the live version
    if (targetVersion === editingItem.live_version) {
      setMessage({ type: 'info', text: `Version ${targetVersion} is already the live version` });
      return;
    }

    if (!window.confirm(`Are you sure you want to set version ${targetVersion} as the live version?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.put(
        `${app_url}/api/dev/imported-sections/${selectedStore}/${editingItem.section_id}/set-live-version`,
        { live_version: targetVersion },
        { withCredentials: true }
      );

      if (response.data && response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `✅ Successfully set version ${targetVersion} as the live version!` 
        });
        // Refresh the imported sections list to show updated data
        await fetchImportedSections();
        // Update editingItem to reflect the new live version
        setEditingItem(prev => ({
          ...prev,
          live_version: targetVersion
        }));
      } else {
        setMessage({ type: 'error', text: response.data?.message || 'Failed to set live version' });
      }
    } catch (error) {
      console.error('Error setting live version:', error);
      setMessage({ 
        type: 'error', 
        text: error?.response?.data?.message || 'Error setting live version. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImportedSection = async (sectionId) => {
    if (!sectionId) {
      setMessage({ type: 'error', text: 'Section ID is required' });
      return;
    }

    // Find section name for better confirmation message
    const section = importedSections.find(s => s.section_id === sectionId);
    const sectionName = section?.section_name || sectionId;

    if (!window.confirm(`Are you sure you want to delete "${sectionName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    if (!selectedStore) {
      setMessage({ type: 'error', text: 'Store is required' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' }); // Clear previous messages
      
      const response = await axios.delete(
        app_url + `/api/dev/imported-sections/${selectedStore}/${sectionId}`,
        { withCredentials: true }
      );

      if (response.data && response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `✅ Imported section "${sectionName}" deleted successfully!` 
        });
        // Refresh the list immediately
        await fetchImportedSections();
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data?.message || 'Delete failed. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error deleting imported section:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error.message || 'Error deleting imported section';
      setMessage({ 
        type: 'error', 
        text: `❌ Failed to delete section: ${errorMessage}` 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleHtmlFileChange = (e) => {
    const file = e.target.files[0];
    setHtmlAnalysisReport(null); // clear report when file changes
    if (file) {
      if (!file.name.endsWith('.html') && file.type !== 'text/html') {
        setMessage({ type: 'error', text: 'Please select an HTML file' });
        return;
      }
      setSelectedHtmlFile(file);
    } else {
      setSelectedHtmlFile(null);
    }
  };

  const handleValidateHtmlFile = () => {
    if (!selectedHtmlFile) {
      setMessage({ type: 'error', text: 'Please select an HTML file first' });
      return;
    }
    setValidatingHtml(true);
    setMessage({ type: '', text: '' });
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const htmlContent = e.target?.result;
        if (typeof htmlContent !== 'string') {
          setHtmlAnalysisReport({ fileName: selectedHtmlFile.name, errors: [{ rule: 'file', message: 'Could not read file as text.' }], warnings: [] });
          return;
        }
        const { errors, warnings } = analyzeImportedSectionHtml(htmlContent, formData);
        setHtmlAnalysisReport({ fileName: selectedHtmlFile.name, errors, warnings });
        if (errors.length === 0) {
          setMessage({ type: 'success', text: 'Validation passed. You can upload the HTML file.' });
        } else {
          setMessage({ type: 'error', text: `Validation found ${errors.length} error(s). Fix them and try again.` });
        }
      } catch (err) {
        setHtmlAnalysisReport({ fileName: selectedHtmlFile.name, errors: [{ rule: 'file', message: err?.message || 'Validation failed.' }], warnings: [] });
        setMessage({ type: 'error', text: 'Validation failed.' });
      } finally {
        setValidatingHtml(false);
      }
    };
    reader.onerror = () => {
      setHtmlAnalysisReport({ fileName: selectedHtmlFile.name, errors: [{ rule: 'file', message: 'Failed to read file.' }], warnings: [] });
      setMessage({ type: 'error', text: 'Failed to read file.' });
      setValidatingHtml(false);
    };
    reader.readAsText(selectedHtmlFile);
  };

  const uploadHtmlToFirebase = async () => {
    if (!selectedHtmlFile) {
      setMessage({ type: 'error', text: 'Please select an HTML file first' });
      return;
    }

    if (!selectedStore) {
      setMessage({ type: 'error', text: 'Please select a store first' });
      return;
    }

    if (!formData.section_id) {
      setMessage({ type: 'error', text: 'Section ID is required. Please fill in Section ID first.' });
      return;
    }

    try {
      setUploadingHtml(true);
      
      // Read HTML file content
      const reader = new FileReader();
      const htmlContent = await new Promise((resolve, reject) => {
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(selectedHtmlFile);
      });

      // If editing, use new endpoint that doesn't create versions
      // If adding new, use the original endpoint that creates version 1
      const isEditing = editingItem && editingItem.section_id;
      const endpoint = isEditing 
        ? `${app_url}/api/firebase/upload_section_file_only`
        : `${app_url}/api/firebase/upload_section`;
      
      // Get the exact client_folder from MongoDB to match the existing path
      let exactClientFolder = Cookies.get('shop');
      let targetVersion = null;
      
      if (isEditing) {
        // Ensure selectedVersionId is set - use live version if not set
        let versionIdToUse = selectedVersionId;
        if (!versionIdToUse && editingItem.versions && editingItem.versions.length > 0) {
          const liveVersion = editingItem.versions.find(v => v.version === editingItem.live_version);
          if (liveVersion) {
            versionIdToUse = liveVersion.section_version_id;
            console.log(`⚠️ selectedVersionId not set, defaulting to live version: ${versionIdToUse}`);
          }
        }
        
        if (!versionIdToUse) {
          setMessage({ type: 'error', text: 'Please select a version first before uploading HTML file' });
          setUploadingHtml(false);
          return;
        }
        
        // Get the selected version
        const selectedVersion = editingItem.versions?.find(v => v.section_version_id === versionIdToUse);
        
        if (selectedVersion) {
          targetVersion = selectedVersion.version;
          console.log(`✅ Using selected version ${targetVersion} (${selectedVersion.section_version_id}) for upload`);
          if (selectedVersion.client_folder) {
            exactClientFolder = selectedVersion.client_folder;
            console.log(`Using exact client_folder from selected version: ${exactClientFolder}`);
          }
        } else {
          // Fallback to live version
          const liveVersion = editingItem.versions?.find(v => v.version === editingItem.live_version);
          if (liveVersion) {
            targetVersion = liveVersion.version;
            console.log(`⚠️ Selected version not found, using live version ${targetVersion} (${liveVersion.section_version_id})`);
            if (liveVersion.client_folder) {
              exactClientFolder = liveVersion.client_folder;
              console.log(`Using exact client_folder from live version: ${exactClientFolder}`);
            }
          } else {
            console.error('❌ No versions found! Cannot proceed with upload.');
            setMessage({ type: 'error', text: 'No versions found for this section. Cannot upload HTML file.' });
            setUploadingHtml(false);
            return;
          }
        }
        
        // Ensure targetVersion is set
        if (!targetVersion) {
          console.error('❌ targetVersion is not set! Cannot proceed.');
          setMessage({ type: 'error', text: 'Unable to determine version for upload. Please try again.' });
          setUploadingHtml(false);
          return;
        }
      } else {
        // For new sections, default to version 1
        targetVersion = 1;
      }
      
      const payload = isEditing
        ? {
            client_folder: exactClientFolder, // Use exact client_folder from MongoDB
            section_folder: formData.section_id,
            html_content: htmlContent,
            version: targetVersion // Use selected version or live version
          }
        : {
            client_folder: Cookies.get('shop'),
            section_folder: formData.section_id,
            html_content: htmlContent,
            css_content: '',
            section_type: formData.section_type || 'assets'
          };

      // Log upload details for debugging
      console.log('🔍 Upload Debug Info:', {
        selectedVersionId,
        targetVersion,
        targetVersionType: typeof targetVersion,
        isEditing,
        sectionId: formData.section_id,
        expectedPath: isEditing ? `${exactClientFolder}/${formData.section_id}/v${targetVersion}/index.html` : 'N/A',
        selectedVersion: isEditing ? editingItem.versions?.find(v => v.section_version_id === selectedVersionId) : null,
        payloadVersion: payload.version,
        payloadVersionType: typeof payload.version
      });

      const response = await axios.post(
        endpoint,
        payload,
        {
          withCredentials: true,
          headers: {
            firebase_token: Cookies.get('firebase_token') || sessionStorage.getItem('firebase_token') || ''
          }
        }
      );

      if (response.data && response.data.success) {
        // Extract file URL from response
        const fileUrl = response.data.data?.url || 
                        response.data.data?.fileUrl || 
                        response.data.data?.html_url ||
                        response.data.data?.publicUrl ||
                        response.data.data?.file_url;
        
        if (fileUrl) {
          // Verify the URL contains the correct version path
          const expectedVersionPath = `/v${targetVersion}/`;
          if (!fileUrl.includes(expectedVersionPath)) {
            console.warn(`⚠️ Warning: Uploaded URL does not contain expected version path ${expectedVersionPath}`);
            console.warn(`   URL: ${fileUrl}`);
            console.warn(`   Expected version: ${targetVersion}`);
          } else {
            console.log(`✅ Verified: URL contains correct version path ${expectedVersionPath}`);
          }
          
          // If editing an existing section, update the selected version's file_url
          if (editingItem && editingItem.section_id) {
            try {
              // Use the same targetVersion that was used for upload (already determined above)
              // This ensures consistency between upload and database update
              const originalVersion = targetVersion;
              
              console.log(`📝 Updating MongoDB: version ${originalVersion} with URL: ${fileUrl}`);
              
              // Call the endpoint to update the selected version's file_url
              // Use new endpoint for any version, or live-version endpoint if updating live version
              const isLiveVersion = targetVersion === editingItem.live_version;
              const endpoint = isLiveVersion
                ? `${app_url}/api/dev/imported-sections/${selectedStore}/${editingItem.section_id}/update-live-version`
                : `${app_url}/api/dev/imported-sections/${selectedStore}/${editingItem.section_id}/update-version`;
              
              const updatePayload = isLiveVersion
                ? { 
                    file_url: fileUrl,
                    original_live_version: originalVersion
                  }
                : {
                    file_url: fileUrl,
                    target_version: originalVersion
                  };
              
              const updateResponse = await axios.put(
                endpoint,
                updatePayload,
                { withCredentials: true }
              );

              if (updateResponse.data && updateResponse.data.success) {
                // Update formData with the cache-busted file URL from response
                const updatedFileUrl = updateResponse.data.data?.file_url || fileUrl;
                setFormData(prev => ({
                  ...prev,
                  file_url: updatedFileUrl
                }));
                
                // Set flag to indicate HTML was uploaded successfully
                setHtmlUploadedSuccessfully(true);
                
                // Show success message with version info
                const versionNumber = originalVersion || 'N/A';
                // Use isLiveVersion to determine if this is the live version or a selected version
                const versionLabel = isLiveVersion ? `live version ${versionNumber}` : `version ${versionNumber}`;
                const messageText = `✅ HTML file uploaded and ${versionLabel} file URL updated! You can continue editing other fields and click "Update" to save all changes.`;
                
                setMessage({ 
                  type: 'success', 
                  text: messageText
                });
                
                // DON'T close modal - let user continue editing other fields
                // DON'T refresh yet - wait until Update button is clicked
              } else {
                throw new Error(updateResponse.data?.message || 'Failed to update live version');
              }
            } catch (updateError) {
              console.error('Error updating live version:', updateError);
              setMessage({ 
                type: 'error', 
                text: `⚠️ HTML file uploaded to Firebase, but failed to update live version in database: ${updateError?.response?.data?.message || updateError.message}. Please try updating again or contact support.` 
              });
              // Still update formData even if backend update failed
              setFormData(prev => ({
                ...prev,
                file_url: fileUrl
              }));
              setHtmlUploadedSuccessfully(false);
            }
          } else {
            // For new sections, just update formData
            setFormData(prev => ({
              ...prev,
              file_url: fileUrl
            }));
            setMessage({ 
              type: 'success', 
              text: '✅ HTML file uploaded successfully! File URL has been set. You can now save the section.' 
            });
            setHtmlUploadedSuccessfully(true);
          }
          
          setSelectedHtmlFile(null);
          // Reset file input
          const fileInput = document.querySelector('input[type="file"][accept*="html"]');
          if (fileInput) fileInput.value = '';
        } else {
          console.error('Firebase response:', response.data);
          throw new Error('File URL not found in response. Check console for response details.');
        }
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }
      } catch (error) {
        console.error('Error uploading HTML file:', error);
        setMessage({ 
          type: 'error', 
          text: `❌ Error uploading HTML file: ${error?.response?.data?.message || error.message || 'Unknown error occurred. Please try again.'}` 
        });
        setHtmlUploadedSuccessfully(false);
      } finally {
        setUploadingHtml(false);
      }
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    if (!selectedStore || !selectedLandingPageUrl) return;

    try {
      setLoading(true);
      const fieldName = getFieldName();
      // Use shop_name cookie if available (matches MongoDB), otherwise use selectedStore
      const shopToUse = Cookies.get('shop_name') || selectedStore;
      const response = await axios.post(
        app_url + `/api/dev/store-data/delete-item`,
        {
          shop: shopToUse,
          landingPageUrl: selectedLandingPageUrl,
          fieldName: fieldName,
          index: index
        },
        { withCredentials: true }
      );
      const data = response.data;
      if (data.success) {
        setMessage({ type: 'success', text: 'Item deleted successfully' });
        await fetchStoreData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to delete item' });
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Error deleting item' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size must be less than 10MB' });
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (sectionId) => {
    if (!selectedImage || !sectionId) {
      return null;
    }

    try {
      setUploadingImage(true);
      // Use shop_name cookie if available (matches MongoDB), otherwise use selectedStore
      const shopToUse = Cookies.get('shop_name') || selectedStore;
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('shop', shopToUse);
      formData.append('landingPageUrl', selectedLandingPageUrl);
      formData.append('sectionId', sectionId);

      const response = await axios.post(
        app_url + `/api/dev/store-data/upload-image`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        return response.data.data.image_url;
      } else {
        throw new Error(response.data.message || 'Image upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Error uploading image' });
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Handle imported sections separately
    if (activeTab === 'imported-sections') {
      if (!selectedStore) {
        setMessage({ type: 'error', text: 'Please select a store first' });
        return;
      }

      if (!formData.section_id || !formData.section_name) {
        setMessage({ type: 'error', text: 'Section ID and Section Name are required' });
        return;
      }

      try {
        setLoading(true);
        
        // Determine target version for version-specific updates (only when editing)
        let targetVersion = null;
        if (editingItem) {
          if (selectedVersionId && editingItem.versions) {
            const selectedVersion = editingItem.versions.find(v => v.section_version_id === selectedVersionId);
            if (selectedVersion) {
              targetVersion = selectedVersion.version;
            }
          }
          // If no version selected but editing, default to live version
          if (!targetVersion && editingItem.live_version) {
            targetVersion = editingItem.live_version;
          }
        }
        
        // Prepare submit data - include ALL fields (query selector, base URL, and file_url)
        // This ensures all changes are saved together, even if HTML was uploaded separately
        const submitData = {
          section_id: formData.section_id,
          section_name: formData.section_name,
          section_type: formData.section_type || 'assets',
          rearrangement_type: formData.rearrangement_type || 'dynamic',
          section_query_selector: formData.section_query_selector || null,
          section_original_url: formData.section_original_url || null,
          file_url: formData.file_url || '' // Include file_url from formData (updated after HTML upload)
        };
        
        // Include target_version only when editing (for version-specific updates)
        if (editingItem && targetVersion !== null) {
          submitData.target_version = targetVersion;
        }

        const url = editingItem
          ? `/api/dev/imported-sections/${selectedStore}/${formData.section_id}`
          : `/api/dev/imported-sections/${selectedStore}`;
        
        const method = editingItem ? 'put' : 'post';
        const response = await axios[method](app_url + url, submitData, { withCredentials: true });
        const data = response.data;
        
        if (data.success) {
          setMessage({ 
            type: 'success', 
            text: editingItem 
              ? '✅ Imported section updated successfully! All changes (query selector, base URL, and file URL) have been saved.' 
              : '✅ Imported section added successfully! The new section has been created.' 
          });
          
          // Close modal and reload data only when Update button is clicked
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
          setSelectedHtmlFile(null);
          setHtmlUploadedSuccessfully(false);
          await fetchImportedSections();
        } else {
          setMessage({ type: 'error', text: data.message || 'Operation failed' });
        }
      } catch (error) {
        console.error('Error saving imported section:', error);
        setMessage({ 
          type: 'error', 
          text: `❌ Error saving imported section: ${error?.response?.data?.message || error.message}` 
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Original logic for other tabs
    if (!selectedStore || !selectedLandingPageUrl) {
      setMessage({ type: 'error', text: 'Please select a store and landing page URL first' });
      return;
    }

    try {
      setLoading(true);
      const fieldName = getFieldName();
      
      // For sections (rearrange-section or approved-section), upload image if selected
      let imageUrl = formData.image_url || '';
      if ((activeTab === 'rearrange-section' || activeTab === 'approved-section') && selectedImage) {
        const sectionId = formData.section_id || (editingItem ? editingItem.section_id : '');
        if (!sectionId) {
          setMessage({ type: 'error', text: 'Section ID is required to upload image' });
          setLoading(false);
          return;
        }
        const uploadedImageUrl = await uploadImage(sectionId);
        if (uploadedImageUrl) {
          imageUrl = uploadedImageUrl;
        } else {
          setLoading(false);
          return; // Error already shown in uploadImage
        }
      }

      // Prepare data with image URL
      // Remove file_url for rearrange-section and approved-section as it's not in MongoDB collection
      const { file_url, ...formDataWithoutFileUrl } = formData;
      const submitData = {
        ...(activeTab === 'rearrange-section' || activeTab === 'approved-section' 
          ? formDataWithoutFileUrl 
          : formData),
        image_url: imageUrl
      };

      // Preserve file_url from original item if it exists (for approved-section and rearrange-section)
      if ((activeTab === 'rearrange-section' || activeTab === 'approved-section') && editingItem && editingItem.file_url) {
        submitData.file_url = editingItem.file_url;
      }

      // Use shop_name cookie if available (matches MongoDB), otherwise use selectedStore
      const shopToUse = Cookies.get('shop_name') || selectedStore;
      
      if (editingItem) {
        // Update existing item
        const response = await axios.put(
          app_url + `/api/dev/store-data/update-item`,
          {
            shop: shopToUse,
            landingPageUrl: selectedLandingPageUrl,
            fieldName: fieldName,
            index: editingItem.index,
            itemData: submitData
          },
          { withCredentials: true }
        );
        const data = response.data;
        
        if (data.success) {
          setMessage({ type: 'success', text: 'Item updated successfully' });
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
          await fetchStoreData();
        } else {
          setMessage({ type: 'error', text: data.message || 'Operation failed' });
        }
      } else {
        // Add new item
        const response = await axios.post(
          app_url + `/api/dev/store-data/add-item`,
          {
            shop: shopToUse,
            landingPageUrl: selectedLandingPageUrl,
            fieldName: fieldName,
            itemData: submitData
          },
          { withCredentials: true }
        );
        const data = response.data;
        
        if (data.success) {
          setMessage({ type: 'success', text: 'Item added successfully' });
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
          await fetchStoreData();
        } else {
          setMessage({ type: 'error', text: data.message || 'Operation failed' });
        }
      }
    } catch (error) {
      console.error('Error saving item:', error);
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Error saving item' });
    } finally {
      setLoading(false);
    }
  };

  const handleSpreadsheetSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStore) {
      setMessage({ type: 'error', text: 'Please select a store first' });
      return;
    }

    try {
      setLoading(true);
      // Use shop_name cookie if available (matches MongoDB), otherwise use selectedStore
      const shopToUse = Cookies.get('shop_name') || selectedStore;
      console.log('Updating spreadsheet data for shop:', shopToUse);
      
      const response = await axios.put(
        app_url + `/api/dev/store-data/${shopToUse}/lp_spreadsheet_details`,
        spreadsheetData,
        { withCredentials: true }
      );
      const data = response.data;
      
      if (data.success) {
        setMessage({ type: 'success', text: 'Spreadsheet details updated successfully' });
        await fetchSpreadsheetData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Operation failed' });
      }
    } catch (error) {
      console.error('Error saving spreadsheet details:', error);
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Error saving spreadsheet details' });
    } finally {
      setLoading(false);
    }
  };

  const getFilteredItems = () => {
    const items = getCurrentItems();
    if (!searchTerm) return items;
    
    return items.filter(item => {
      return (
        (item.section_name && item.section_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.section_id && item.section_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.section_description && item.section_description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });
  };

  const getPaginatedItems = () => {
    const filtered = getFilteredItems();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(getFilteredItems().length / itemsPerPage);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderSpreadsheetForm = () => {
    if (!selectedStore) {
      return (
        <div className="dev-section">
          <p className="dev-no-data">Please select a store from the sidebar to view spreadsheet details</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="dev-section">
          <div className="dev-loading-content">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading data...</p>
          </div>
        </div>
      );
    }

    // Check if spreadsheet details already exist
    const hasExistingData = lpSpreadsheetDetails && (lpSpreadsheetDetails.spreadsheetUrl || lpSpreadsheetDetails.spreadsheetId);
    const hasChanges = hasExistingData && (
      spreadsheetData.spreadsheetUrl !== lpSpreadsheetDetails?.spreadsheetUrl ||
      spreadsheetData.spreadsheetId !== lpSpreadsheetDetails?.spreadsheetId
    );

    return (
      <div className="dev-section">
        {/* Status Card */}
        {hasExistingData && (
          <div className="alert alert-info mb-4" role="alert">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>
                  <i className="bi bi-info-circle me-2"></i>
                  Spreadsheet Details Already Configured
                </strong>
                <p className="mb-0 mt-1 small">
                  {lpSpreadsheetDetails.updated_at 
                    ? `Last updated: ${new Date(lpSpreadsheetDetails.updated_at).toLocaleString()}`
                    : 'Existing configuration found'}
                </p>
              </div>
              <div>
                <span className="badge bg-success">Active</span>
              </div>
            </div>
          </div>
        )}

        {!hasExistingData && (
          <div className="alert alert-warning mb-4" role="alert">
            <strong>
              <i className="bi bi-exclamation-triangle me-2"></i>
              No Spreadsheet Details Found
            </strong>
            <p className="mb-0 mt-1 small">Please add spreadsheet URL and ID to configure.</p>
          </div>
        )}

        <Form onSubmit={handleSpreadsheetSubmit}>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <FormLabel>
                  Spreadsheet URL *
                  {hasExistingData && spreadsheetData.spreadsheetUrl === lpSpreadsheetDetails?.spreadsheetUrl && (
                    <span className="badge bg-secondary ms-2">Current</span>
                  )}
                </FormLabel>
                <FormControl
                  type="url"
                  name="spreadsheetUrl"
                  value={spreadsheetData.spreadsheetUrl}
                  onChange={(e) => setSpreadsheetData({ ...spreadsheetData, spreadsheetUrl: e.target.value })}
                  required
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  style={{
                    borderColor: hasExistingData && spreadsheetData.spreadsheetUrl !== lpSpreadsheetDetails?.spreadsheetUrl 
                      ? '#ffc107' 
                      : undefined
                  }}
                />
                {hasExistingData && spreadsheetData.spreadsheetUrl !== lpSpreadsheetDetails?.spreadsheetUrl && (
                  <small className="text-warning d-block mt-1">
                    <i className="bi bi-pencil me-1"></i>
                    This value will be updated
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <FormLabel>
                  Spreadsheet ID *
                  {hasExistingData && spreadsheetData.spreadsheetId === lpSpreadsheetDetails?.spreadsheetId && (
                    <span className="badge bg-secondary ms-2">Current</span>
                  )}
                </FormLabel>
                <FormControl
                  type="text"
                  name="spreadsheetId"
                  value={spreadsheetData.spreadsheetId}
                  onChange={(e) => setSpreadsheetData({ ...spreadsheetData, spreadsheetId: e.target.value })}
                  required
                  placeholder="1zsqcTksOmSvZPM0lVD-KfaPHu-JV8b3HWZdDo-_SuWg"
                  style={{
                    borderColor: hasExistingData && spreadsheetData.spreadsheetId !== lpSpreadsheetDetails?.spreadsheetId 
                      ? '#ffc107' 
                      : undefined
                  }}
                />
                {hasExistingData && spreadsheetData.spreadsheetId !== lpSpreadsheetDetails?.spreadsheetId && (
                  <small className="text-warning d-block mt-1">
                    <i className="bi bi-pencil me-1"></i>
                    This value will be updated
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
          <div className="dev-form-actions">
            <div className="d-flex gap-2 align-items-center">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading 
                  ? (hasExistingData ? 'Updating...' : 'Saving...') 
                  : (hasExistingData ? 'Update Spreadsheet Details' : 'Save Spreadsheet Details')}
              </Button>
              {hasExistingData && hasChanges && (
                <Button 
                  variant="outline-secondary" 
                  type="button"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to reset the form to current values?')) {
                      setSpreadsheetData({
                        spreadsheetUrl: lpSpreadsheetDetails?.spreadsheetUrl || '',
                        spreadsheetId: lpSpreadsheetDetails?.spreadsheetId || ''
                      });
                    }
                  }}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-counterclockwise me-1"></i>
                  Reset to Current
                </Button>
              )}
            </div>
            {hasExistingData && (
              <small className="text-muted d-block mt-2">
                <i className="bi bi-info-circle me-1"></i>
                You are updating existing spreadsheet details. Changes will be saved immediately.
              </small>
            )}
          </div>
        </Form>

        {/* Current Details Display (Read-only view) */}
        {hasExistingData && (
          <div className="mt-4 p-3 bg-light rounded">
            <h6 className="mb-3">
              <i className="bi bi-file-earmark-spreadsheet me-2"></i>
              Current Configuration
            </h6>
            <Row>
              <Col md={6}>
                <div className="mb-2">
                  <strong>Spreadsheet URL:</strong>
                  <div className="mt-1">
                    {lpSpreadsheetDetails.spreadsheetUrl ? (
                      <a 
                        href={lpSpreadsheetDetails.spreadsheetUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-break"
                      >
                        {lpSpreadsheetDetails.spreadsheetUrl}
                      </a>
                    ) : (
                      <span className="text-muted">Not set</span>
                    )}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <strong>Spreadsheet ID:</strong>
                  <div className="mt-1">
                    {lpSpreadsheetDetails.spreadsheetId ? (
                      <code>{lpSpreadsheetDetails.spreadsheetId}</code>
                    ) : (
                      <span className="text-muted">Not set</span>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    );
  };

  const renderImportedSectionsTable = () => {
    if (!selectedStore) {
      return (
        <div className="dev-section">
          <p className="dev-no-data">Please select a store from the sidebar to view imported sections</p>
        </div>
      );
    }

    // Filter imported sections based on search term
    const filteredSections = importedSections.filter(section => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        (section.section_name && section.section_name.toLowerCase().includes(searchLower)) ||
        (section.section_id && section.section_id.toLowerCase().includes(searchLower)) ||
        (section.section_query_selector && section.section_query_selector.toLowerCase().includes(searchLower)) ||
        (section.section_original_url && section.section_original_url.toLowerCase().includes(searchLower))
      );
    });

    // Paginate filtered sections
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedSections = filteredSections.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filteredSections.length / itemsPerPage);

    return (
      <div className="dev-section">
        {loading && (
          <div className="dev-loading-content mb-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading data...</p>
          </div>
        )}

        <div className="dev-controls-row">
          <Row>
            <Col md={6}>
              <InputGroup className="dev-search-group">
                <InputGroup.Text className="dev-search-icon">🔍</InputGroup.Text>
                <FormControl
                  type="text"
                  placeholder="Search by name, ID, query selector, or URL..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="dev-search-input"
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    title="Clear search"
                  >
                    ×
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>
        </div>

        <div className="dev-table-container">
          <Table className="dev-table" striped bordered hover>
            <thead>
              <tr>
                <th className="d-none">Section ID</th>
                <th>Section Name</th>
                <th>Type</th>
                <th className="d-none">Rearrangement</th>
                <th>Live Version</th>
                <th className="d-none">Total Versions</th>
                <th className="d-none">Query Selector</th>
                <th className="d-none">Original URL</th>
                <th className="d-none">File URL</th>
                <th className="d-none">Updated At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedSections.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center py-4">
                    <div className="dev-no-data">
                      {searchTerm ? (
                        <>
                          <p className="mb-2">No imported sections found matching "{searchTerm}"</p>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setSearchTerm('');
                              setCurrentPage(1);
                            }}
                          >
                            Clear Search
                          </Button>
                        </>
                      ) : (
                        <p>No imported sections found. Sections will appear here once they are imported.</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedSections.map((section, index) => {
                  const actualIndex = startIndex + index;
                  const liveVersion = section.versions?.find(v => v.version === section.live_version) || section.versions?.[0];
                  
                  return (
                    <tr key={`${section.section_id}-${section.live_version}-${section.updated_at || actualIndex}`} className={actualIndex % 2 === 0 ? 'dev-table-row-even' : 'dev-table-row-odd'}>
                      <td className="d-none">{section.section_id || '-'}</td>
                      <td>{section.section_name || '-'}</td>
                      <td>
                        <span className={`dev-badge dev-badge-${section.section_type || 'assets'}`}>
                          {section.section_type || 'assets'}
                        </span>
                      </td>
                      <td className="d-none">
                        <span className={`dev-badge dev-badge-${section.rearrangement_type || 'dynamic'}`}>
                          {section.rearrangement_type || 'dynamic'}
                        </span>
                      </td>
                      <td>
                        <span className="dev-badge dev-badge-primary">
                          v{section.live_version || '-'}
                        </span>
                      </td>
                      <td className="d-none">{section.versions?.length || 0}</td>
                      <td className="d-none">
                        {liveVersion?.section_query_selector || section.section_query_selector ? (
                          <span title={liveVersion?.section_query_selector || section.section_query_selector}>
                            {(liveVersion?.section_query_selector || section.section_query_selector).length > 30 
                              ? (liveVersion?.section_query_selector || section.section_query_selector).substring(0, 30) + '...' 
                              : (liveVersion?.section_query_selector || section.section_query_selector)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="d-none">
                        {liveVersion?.section_original_url || section.section_original_url ? (
                          <a href={liveVersion?.section_original_url || section.section_original_url} target="_blank" rel="noopener noreferrer" className="dev-link">
                            {(liveVersion?.section_original_url || section.section_original_url).length > 30 
                              ? (liveVersion?.section_original_url || section.section_original_url).substring(0, 30) + '...' 
                              : (liveVersion?.section_original_url || section.section_original_url)}
                          </a>
                        ) : '-'}
                      </td>
                      <td className="d-none">
                        {liveVersion?.file_url ? (
                          <a href={ensureCacheBuster(liveVersion.file_url)} target="_blank" rel="noopener noreferrer" className="dev-link">
                            View HTML
                          </a>
                        ) : '-'}
                      </td>
                      <td className="d-none">
                        {section.updated_at 
                          ? new Date(section.updated_at).toLocaleDateString() 
                          : '-'}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditImportedSection(section, actualIndex)}
                          className="me-2"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteImportedSection(section.section_id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        {!loading && filteredSections.length > 0 && totalPages > 1 && (
          <div className="dev-pagination-container">
            <Pagination className="dev-pagination">
              <Pagination.Prev
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              />
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <Pagination.Item
                  key={page}
                  active={page === currentPage}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              />
            </Pagination>
            <p className="dev-pagination-info">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredSections.length)} of {filteredSections.length} sections
              {searchTerm && ` (filtered from ${importedSections.length} total)`}
            </p>
          </div>
        )}
        {!loading && filteredSections.length > 0 && totalPages === 1 && searchTerm && (
          <div className="dev-pagination-container">
            <p className="dev-pagination-info">
              Showing {filteredSections.length} of {importedSections.length} sections {searchTerm && `(filtered by "${searchTerm}")`}
            </p>
          </div>
        )}
      </div>
    );
  };

  const fetchCustomEvents = async (landing_page) => {
    if (!selectedStore) return;
    setCustomEventsListLoading(true);
    try {
      const res = await axios.get(`${app_url}/api/dev/custom-events?shop=${encodeURIComponent(selectedStore)}&landing_page=${encodeURIComponent(landing_page || '')}`, { withCredentials: true });
      setCustomEvents(res.data?.data || []);
    } catch (err) {
      toast.error('Failed to fetch custom events');
    } finally {
      setCustomEventsListLoading(false);
    }
  };

  const handleCreateCustomEvent = async () => {
    if (!customEventForm.landing_page || !customEventForm.event_name || !customEventForm.script) {
      toast.error('Landing page, event name, and script are required');
      return;
    }
    const isDuplicate = customEvents.some(
      e => e.event_name?.toLowerCase() === customEventForm.event_name?.toLowerCase() &&
           e.landing_page_url === customEventForm.landing_page &&
           e.is_active !== false
    );
    if (isDuplicate) {
      toast.error('An active event with this name already exists on the selected landing page.');
      return;
    }
    const report = analyzeCustomEventScript(customEventForm.script);
    setScriptValidationReport(report);
    if (report.errors.length > 0) {
      toast.error('Script has validation errors. Please fix them before submitting.');
      return;
    }
    setCustomEventLoading(true);
    try {
      await axios.post(`${app_url}/api/dev/custom-events`, {
        shop: selectedStore,
        event_name: customEventForm.event_name,
        script: customEventForm.script,
        landing_page: customEventForm.landing_page
      }, { withCredentials: true });
      toast.success('Custom event created successfully');
      setCustomEventForm({ landing_page: '', event_name: '', script: '' });
      setScriptValidationReport(null);
      fetchCustomEvents(customEventsFilterLP);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create custom event');
    } finally {
      setCustomEventLoading(false);
    }
  };

  const handleCustomEventsFilterChange = (lp) => {
    setCustomEventsFilterLP(lp);
    fetchCustomEvents(lp);
  };

  const handleEditCustomEvent = (event) => {
    setEditingCustomEvent(event);
    setEditCustomEventForm({ event_name: event.event_name, script: event.script, is_active: event.is_active !== false });
    setShowEditCustomEventModal(true);
  };

  const handleUpdateCustomEvent = async () => {
    if (!editCustomEventForm.event_name || !editCustomEventForm.script) {
      toast.error('Event name and script are required');
      return;
    }
    const isDuplicate = customEvents.some(
      e => e.event_name?.toLowerCase() === editCustomEventForm.event_name?.toLowerCase() &&
           e.landing_page_url === editingCustomEvent.landing_page_url &&
           e.event_id !== editingCustomEvent.event_id &&
           e.is_active !== false
    );
    if (isDuplicate) {
      toast.error('An active event with this name already exists on this landing page.');
      return;
    }
    const report = analyzeCustomEventScript(editCustomEventForm.script);
    setEditScriptValidationReport(report);
    if (report.errors.length > 0) {
      toast.error('Script has validation errors. Please fix them before saving.');
      return;
    }
    setEditCustomEventLoading(true);
    try {
      const res = await axios.put(`${app_url}/api/dev/custom-events/${editingCustomEvent.event_id}`, {
        shop: selectedStore,
        event_name: editCustomEventForm.event_name,
        script: editCustomEventForm.script,
        is_active: editCustomEventForm.is_active ? 1 : 0,
        landing_page: editingCustomEvent.landing_page_url
      }, { withCredentials: true });
      if (res.data?.success === false) {
        toast.error(res.data.message || 'Failed to update custom event');
        return;
      }
      toast.success('Custom event updated successfully');
      setShowEditCustomEventModal(false);
      setEditScriptValidationReport(null);
      fetchCustomEvents(customEventsFilterLP);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update custom event');
    } finally {
      setEditCustomEventLoading(false);
    }
  };

  const handleDeleteCustomEvent = async (event) => {
    if (!window.confirm(`Deactivate event "${event.event_name}"? It will remain in the list but stop firing.`)) return;
    try {
      await axios.delete(`${app_url}/api/dev/custom-events/${event.event_id}`, {
        data: { shop: selectedStore },
        withCredentials: true
      });
      toast.success('Custom event deactivated');
      fetchCustomEvents(customEventsFilterLP);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to deactivate custom event');
    }
  };

  const renderCustomEvents = () => (
    <div className="dev-content">
      <div className="dev-section">
        <div className="dev-section-header mb-4">
          <h2 className="dev-section-title">Custom Events</h2>
        </div>

        <Card className="mb-4">
          <Card.Header><strong>Create Custom Event</strong></Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <FormLabel>Landing Page</FormLabel>
                  <FormControl
                    as="select"
                    value={customEventForm.landing_page}
                    onChange={(e) => setCustomEventForm(prev => ({ ...prev, landing_page: e.target.value }))}
                    className="dev-select"
                  >
                    <option value="">-- Select landing page --</option>
                    {landingPageUrls.map(url => (
                      <option key={url} value={url}>{url}</option>
                    ))}
                  </FormControl>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <FormLabel>Event Name</FormLabel>
                  <FormControl
                    type="text"
                    placeholder="e.g. cta_click"
                    value={customEventForm.event_name}
                    onChange={(e) => setCustomEventForm(prev => ({ ...prev, event_name: e.target.value }))}
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup className="mb-3">
              <FormLabel>Script</FormLabel>
              <FormControl
                as="textarea"
                rows={8}
                placeholder="Enter your custom event script..."
                value={customEventForm.script}
                onChange={(e) => { setCustomEventForm(prev => ({ ...prev, script: e.target.value })); setScriptValidationReport(null); }}
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
              {scriptValidationReport && (
                <div className="dev-script-validation-report mt-2 p-2" style={{ border: '1px solid #dee2e6', borderRadius: '6px', background: '#fafafa' }}>
                  {scriptValidationReport.errors.length === 0 && scriptValidationReport.warnings.length === 0 && (
                    <p className="text-success mb-0"><strong>✓ Script looks good</strong></p>
                  )}
                  {scriptValidationReport.errors.length > 0 && (
                    <>
                      <p className="text-danger mb-1"><strong>Errors ({scriptValidationReport.errors.length})</strong></p>
                      <ul className="mb-1 ps-3">
                        {scriptValidationReport.errors.map((e, i) => <li key={i} className="text-danger" style={{ fontSize: '13px' }}>{e}</li>)}
                      </ul>
                    </>
                  )}
                  {scriptValidationReport.warnings.length > 0 && (
                    <>
                      <p className="text-warning mb-1"><strong>Warnings ({scriptValidationReport.warnings.length})</strong></p>
                      <ul className="mb-0 ps-3">
                        {scriptValidationReport.warnings.map((w, i) => <li key={i} className="text-warning" style={{ fontSize: '13px' }}>{w}</li>)}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </FormGroup>
            <Button
              variant="primary"
              onClick={handleCreateCustomEvent}
              disabled={customEventLoading}
              className="dev-create-toggle-btn"
            >
              {customEventLoading ? 'Creating...' : 'Create Event'}
            </Button>
          </Card.Body>
        </Card>

        <div className="mt-4">
          <div className="d-flex align-items-center gap-3 mb-3">
            <h5 className="mb-0">All Custom Events</h5>
            <FormControl
              as="select"
              value={customEventsFilterLP}
              onChange={(e) => handleCustomEventsFilterChange(e.target.value)}
              style={{ maxWidth: '320px' }}
              className="dev-select"
            >
              <option value="">-- All landing pages --</option>
              {landingPageUrls.map(url => (
                <option key={url} value={url}>{url}</option>
              ))}
            </FormControl>
          </div>

          {customEventsListLoading ? (
            <p className="dev-no-data">Loading...</p>
          ) : customEvents.length === 0 ? (
            <p className="dev-no-data">No custom events found.</p>
          ) : (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Event Name</th>
                  <th>Landing Page</th>
                  <th>Script Preview</th>
                  <th>Created At</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customEvents.map((event, idx) => (
                  <tr key={event.event_id || idx}>
                    <td><code>{event.event_name}</code></td>
                    <td style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.landing_page_url}</td>
                    <td><code style={{ fontSize: '11px' }}>{event.script?.substring(0, 80)}{event.script?.length > 80 ? '...' : ''}</code></td>
                    <td>{event.created_at ? new Date(event.created_at).toLocaleString() : '—'}</td>
                    <td>
                      <span className={`badge ${event.is_active !== false ? 'bg-success' : 'bg-secondary'}`}>
                        {event.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => handleEditCustomEvent(event)} className="me-1" disabled={!event.event_id}>Edit</Button>
                      {event.is_active !== false && (
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteCustomEvent(event)} disabled={!event.event_id}>Deactivate</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      {/* Edit Custom Event Modal */}
      <Modal show={showEditCustomEventModal} onHide={() => { setShowEditCustomEventModal(false); setEditScriptValidationReport(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Custom Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup className="mb-3">
            <FormLabel>Event Name</FormLabel>
            <FormControl
              type="text"
              value={editCustomEventForm.event_name}
              onChange={(e) => setEditCustomEventForm(prev => ({ ...prev, event_name: e.target.value }))}
            />
          </FormGroup>
          <FormGroup className="mb-3">
            <FormLabel>Script</FormLabel>
            <FormControl
              as="textarea"
              rows={10}
              value={editCustomEventForm.script}
              onChange={(e) => { setEditCustomEventForm(prev => ({ ...prev, script: e.target.value })); setEditScriptValidationReport(null); }}
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
            {editScriptValidationReport && (
              <div className="dev-script-validation-report mt-2 p-2" style={{ border: '1px solid #dee2e6', borderRadius: '6px', background: '#fafafa' }}>
                {editScriptValidationReport.errors.length === 0 && editScriptValidationReport.warnings.length === 0 && (
                  <p className="text-success mb-0"><strong>✓ Script looks good</strong></p>
                )}
                {editScriptValidationReport.errors.length > 0 && (
                  <>
                    <p className="text-danger mb-1"><strong>Errors ({editScriptValidationReport.errors.length})</strong></p>
                    <ul className="mb-1 ps-3">
                      {editScriptValidationReport.errors.map((e, i) => <li key={i} className="text-danger" style={{ fontSize: '13px' }}>{e}</li>)}
                    </ul>
                  </>
                )}
                {editScriptValidationReport.warnings.length > 0 && (
                  <>
                    <p className="text-warning mb-1"><strong>Warnings ({editScriptValidationReport.warnings.length})</strong></p>
                    <ul className="mb-0 ps-3">
                      {editScriptValidationReport.warnings.map((w, i) => <li key={i} className="text-warning" style={{ fontSize: '13px' }}>{w}</li>)}
                    </ul>
                  </>
                )}
              </div>
            )}
          </FormGroup>
          <FormGroup className="mb-3">
            <FormLabel>Status</FormLabel>
            <div>
              <input
                type="checkbox"
                id="edit-event-active"
                checked={editCustomEventForm.is_active}
                onChange={(e) => setEditCustomEventForm(prev => ({ ...prev, is_active: e.target.checked }))}
                className="me-2"
              />
              <label htmlFor="edit-event-active">Active</label>
            </div>
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleUpdateCustomEvent} disabled={editCustomEventLoading}>
            {editCustomEventLoading ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="secondary" onClick={() => { setShowEditCustomEventModal(false); setEditScriptValidationReport(null); }}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );

  const renderTable = () => {
    const items = getPaginatedItems();
    const allItems = getFilteredItems();

    if (!selectedStore) {
      return (
        <div className="dev-section">
          <p className="dev-no-data">Please select a store from the sidebar to view data</p>
        </div>
      );
    }

    if (!selectedLandingPageUrl) {
      return (
        <div className="dev-section">
          <p className="dev-no-data">Please select a landing page URL to view data</p>
        </div>
      );
    }

    return (
      <div className="dev-section">
        {loading && (
          <div className="dev-loading-content mb-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading data...</p>
          </div>
        )}

        <div className="dev-controls-row">
          <Row>
            <Col md={6}>
              <InputGroup className="dev-search-group">
                <InputGroup.Text className="dev-search-icon">🔍</InputGroup.Text>
                <FormControl
                  type="text"
                  placeholder="Search by name, ID, or description..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="dev-search-input"
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    title="Clear search"
                  >
                    ×
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>
        </div>

        <div className="dev-table-container">
          <Table className="dev-table" striped bordered hover>
            <thead>
              <tr>
                <th>Image</th>
                <th className="d-none">Section ID</th>
                <th>Section Name</th>
                <th>Description</th>
                <th className="d-none">Query Selector</th>
                <th className="d-none">Rearrangement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="dev-no-data">
                      {searchTerm ? (
                        <>
                          <p className="mb-2">No items found matching "{searchTerm}"</p>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setSearchTerm('');
                              setCurrentPage(1);
                            }}
                          >
                            Clear Search
                          </Button>
                        </>
                      ) : (
                        <p>No items found.</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item, index) => {
                  const actualIndex = (currentPage - 1) * itemsPerPage + index;
                  // Use section_id and image_url in key to force re-render when image is updated
                  const rowKey = `${item.section_id || actualIndex}-${item.image_url || ''}`;
                  return (
                    <tr key={rowKey} className={actualIndex % 2 === 0 ? 'dev-table-row-even' : 'dev-table-row-odd'}>
                      <td>
                        {item.image_url ? (
                          <img 
                            src={ensureCacheBuster(item.image_url)} 
                            alt={item.section_name || 'Section preview'} 
                            style={{ 
                              width: '100px', 
                              height: '100px', 
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #dee2e6',
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(ensureCacheBuster(item.image_url), '_blank')}
                            onError={(e) => {
                              e.target.onerror = null; // Prevent infinite loop
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23f8f9fa"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%236c757d" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        ) : (
                          <div 
                            style={{ 
                              width: '100px', 
                              height: '100px', 
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#6c757d',
                              fontSize: '12px',
                              textAlign: 'center',
                              padding: '5px'
                            }}
                          >
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="d-none">{item.section_id || '-'}</td>
                      <td>{item.section_name || '-'}</td>
                      <td>{item.section_description ? (item.section_description.length > 50 ? item.section_description.substring(0, 50) + '...' : item.section_description) : '-'}</td>
                      <td className="d-none">{item.section_query_selector || '-'}</td>
                      <td className="d-none">
                        <span className={`dev-badge dev-badge-${item.section_rearrangement || 'dynamic'}`}>
                          {item.section_rearrangement || 'dynamic'}
                        </span>
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(item, actualIndex)}
                          className="me-2"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(actualIndex)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        {!loading && allItems.length > 0 && getTotalPages() > 1 && (
          <div className="dev-pagination-container">
            <Pagination className="dev-pagination">
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map(page => (
                <Pagination.Item
                  key={page}
                  active={page === currentPage}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === getTotalPages()}
              />
            </Pagination>
            <p className="dev-pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, allItems.length)} of {allItems.length} items
              {searchTerm && ` (filtered from ${getCurrentItems().length} total)`}
            </p>
          </div>
        )}
        {!loading && allItems.length > 0 && getTotalPages() === 1 && searchTerm && (
          <div className="dev-pagination-container">
            <p className="dev-pagination-info">
              Showing {allItems.length} of {getCurrentItems().length} items {searchTerm && `(filtered by "${searchTerm}")`}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Check if user has admin or developer access
  if (!isDeveloperOrAdmin() && !hasPermission('write_users')) {
    return (
      <div className="main-container">
        <div className="dev-header">
          <h1 className="dev-title">Access Denied</h1>
          <p className="dev-subtitle">You don't have permission to access the Dev Panel</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="dev-header main-title mt-4">
        <h2>Dev Panel</h2>
      </div>

      {message.text && (
        <div className={`dev-message dev-message-${message.type}`}>
          {message.text}
          <Button 
            variant="link" 
            className="dev-message-close"
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </Button>
        </div>
      )}

      <Container fluid className="p-0 mt-2">

          {/* Top-level page navigation tabs */}
          <div className="dev-top-tabs mb-3">
            <Nav className="dev-top-tabs-nav">
              {hasAiCampaignCreationAccess && (
                <Nav.Item>
                  <Nav.Link
                    className={activePage === 'ai-campaign-creation' ? 'active' : ''}
                    onClick={() => setActivePage('ai-campaign-creation')}
                  >
                    AI Campaign Creation
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link
                  className={activePage === 'section' ? 'active' : ''}
                  onClick={() => setActivePage('section')}
                >
                  Sections
                </Nav.Link>
              </Nav.Item>
              {hasCustomEventAccess && (
                <Nav.Item>
                  <Nav.Link
                    className={activePage === 'analytics' ? 'active' : ''}
                    onClick={() => { setActivePage('analytics'); fetchCustomEvents(customEventsFilterLP); }}
                  >
                    Analytics
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </div>

          <div>

        {activePage === 'section' && (
          <>
            <div className="dev-tabs-header mb-4">
              <Nav className="dev-tabs-header">
                {devSections.map((section) => (
                  <Nav.Item key={section.id}>
                    <Nav.Link
                      className={activeTab === section.id ? 'active' : ''}
                      onClick={() => handleTabChange(section.id)}
                    >
                      {section.title}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>

            <div className="dev-content">
              <div className="dev-section">
                <div className="dev-section-header">
                  <h2 className="dev-section-title">
                    {activeTab === 'rearrange-section' ? 'Section Rearrangement' :
                     activeTab === 'imported-sections' ? 'Imported Sections' :
                     activeTab === 'approved-section' ? 'Approved Sections' :
                     'Spreadsheet Details'}
                  </h2>
                  <div className="dev-selectors">
                    {selectedStore && (
                      <div className="mb-3 me-3" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FormLabel style={{ margin: 0, fontWeight: 'bold' }}>Store:</FormLabel>
                        <span style={{ color: '#495057' }}>{getShopName()}</span>
                      </div>
                    )}
                    {selectedStore && activeTab !== 'spreadsheet' && activeTab !== 'imported-sections' && (
                      <FormGroup className="mb-3 me-3" style={{ minWidth: '300px' }}>
                        <FormLabel>Landing Page URL</FormLabel>
                        <FormControl
                          as="select"
                          value={selectedLandingPageUrl}
                          onChange={handleLandingPageUrlChange}
                          className="dev-select"
                        >
                          <option value="">-- Select landing page URL --</option>
                          {landingPageUrls.map(url => (
                            <option key={url} value={url}>{url}</option>
                          ))}
                        </FormControl>
                      </FormGroup>
                    )}
                  </div>
                </div>

                {selectedStore && activeTab === 'rearrange-section' && selectedLandingPageUrl && (
                  <div className="dev-section-header">
                    <div></div>
                    <div className="d-flex gap-2">
                      {sectionsRearrangement.length === 0 && !aiSectionGenerated && (
                        <Button
                          variant="outline-primary"
                          onClick={handleAiGenerateSections}
                          disabled={aiSectionGenerating || aiSectionGeneratingOnServer}
                          className="dev-create-toggle-btn"
                        >
                          {(aiSectionGenerating || aiSectionGeneratingOnServer) ? 'Generating...' : '✦ Generate with AI'}
                        </Button>
                      )}
                      <Button
                        variant="primary"
                        onClick={handleAddRearrangeSection}
                        className="dev-create-toggle-btn"
                      >
                        Add new
                      </Button>
                    </div>
                  </div>
                )}
                {selectedStore && activeTab === 'approved-section' && selectedLandingPageUrl && (
                  <div className="dev-section-header">
                    <div></div>
                    <Button
                      variant="primary"
                      onClick={handleGenerateGrowthbookScript}
                      className="dev-create-toggle-btn"
                      disabled={loading}
                    >
                      {loading ? 'Generating...' : 'Generate Growthbook Script'}
                    </Button>
                  </div>
                )}
                {/* Imported sections "Add New" button — currently disabled */}

                {activeTab === 'spreadsheet'
                  ? renderSpreadsheetForm()
                  : activeTab === 'imported-sections'
                  ? renderImportedSectionsTable()
                  : renderTable()}
              </div>
            </div>
          </>
        )}

        {activePage === 'ai-campaign-creation' && hasAiCampaignCreationAccess && (
          <AICampaignCreation
            shop_domain={selectedStore || shop_domain}
            app_url={app_url}
            ViewedPage={ViewedPage}
          />
        )}

        {activePage === 'analytics' && hasCustomEventAccess && renderCustomEvents()}

          </div>
      </Container>

      {/* Edit Modal for Rearrange Section and Approved Section */}
      {(activeTab === 'approved-section' || activeTab === 'rearrange-section') && (
        <Modal show={showEditModal} onHide={() => { setShowEditModal(false); resetForm(); }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {!editingItem && activeTab === 'rearrange-section' ? 'Add Rearrange Section' : `Edit ${activeTab === 'rearrange-section' ? 'Rearrange' : 'Approved'} Section`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section ID *</FormLabel>
                      <FormControl
                        type="text"
                        name="section_id"
                        value={formData.section_id || ''}
                        onChange={handleFormChange}
                        required
                        readOnly={!!editingItem}
                        disabled={!!editingItem}
                        style={editingItem ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : undefined}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Name *</FormLabel>
                      <FormControl
                        type="text"
                        name="section_name"
                        value={formData.section_name || ''}
                        onChange={handleFormChange}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup className="mb-3">
                  <FormLabel>Section Description</FormLabel>
                  <FormControl
                    as="textarea"
                    rows={3}
                    name="section_description"
                    value={formData.section_description || ''}
                    onChange={handleFormChange}
                  />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Query Selector *</FormLabel>
                      <FormControl
                        type="text"
                        name="section_query_selector"
                        value={formData.section_query_selector || ''}
                        onChange={handleFormChange}
                        required
                        readOnly={activeTab === 'approved-section'}
                        disabled={activeTab === 'approved-section'}
                        style={activeTab === 'approved-section' ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : undefined}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Parent Query Selector</FormLabel>
                      <FormControl
                        type="text"
                        name="parent_query_selector"
                        value={formData.parent_query_selector || ''}
                        onChange={handleFormChange}
                        readOnly={activeTab === 'approved-section'}
                        disabled={activeTab === 'approved-section'}
                        style={activeTab === 'approved-section' ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : undefined}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Rearrangement</FormLabel>
                      <FormControl
                        as="select"
                        name="section_rearrangement"
                        value={formData.section_rearrangement || 'dynamic'}
                        onChange={handleFormChange}
                        readOnly={activeTab === 'approved-section'}
                        disabled={activeTab === 'approved-section'}
                        style={activeTab === 'approved-section' ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : undefined}
                      >
                        <option value="dynamic">Dynamic</option>
                        <option value="static">Static</option>
                      </FormControl>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Version</FormLabel>
                      <FormControl
                        type="number"
                        name="version"
                        value={formData.version || 1}
                        onChange={handleFormChange}
                        min="1"
                        readOnly={true}
                        disabled={true}
                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Category</FormLabel>
                      <FormControl
                        as="select"
                        name="section_category"
                        value={formData.section_category || ''}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            section_category: e.target.value,
                            section_sub_category: ''
                          }));
                        }}
                      >
                        <option value="">Select Category</option>
                        {formData.section_category && !sectionsTags.some(tag => tag.category === formData.section_category) && (
                          <option value={formData.section_category}>{formData.section_category} (AI)</option>
                        )}
                        {sectionsTags.map(tag => (
                          <option key={tag.id} value={tag.category}>{tag.category}</option>
                        ))}
                      </FormControl>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Sub Category</FormLabel>
                      <FormControl
                        as="select"
                        name="section_sub_category"
                        value={formData.section_sub_category || ''}
                        onChange={handleFormChange}
                        disabled={!formData.section_category}
                      >
                        <option value="">Select Sub Category</option>
                        {(() => {
                          const subs = sectionsTags.find(tag => tag.category === formData.section_category)?.subcategories || [];
                          const current = formData.section_sub_category;
                          const showCurrentAsAi = current && !subs.includes(current);
                          return (
                            <>
                              {showCurrentAsAi && (
                                <option value={current}>{current} (AI)</option>
                              )}
                              {subs.map((sc, i) => (
                                <option key={i} value={sc}>{sc}</option>
                              ))}
                            </>
                          );
                        })()}
                      </FormControl>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Image</FormLabel>
                      <FormControl
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="mb-2"
                      />
                      {imagePreview && (
                        <div className="mt-2">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                          />
                        </div>
                      )}
                      {formData.image_url && !imagePreview && (
                        <div className="mt-2">
                          <p className="text-muted small">Current image:</p>
                          <img 
                            src={ensureCacheBuster(formData.image_url)} 
                            alt="Current" 
                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    {/* File URL Preview Section */}
                    {editingItem && editingItem.file_url && (
                      <FormGroup className="mb-3">
                        <FormLabel className='mt-3'></FormLabel>
                        {/* <FormControl
                          type="hidden"
                          value={editingItem.file_url}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa', fontSize: '12px' }}
                        /> */}
                        <small className="text-muted d-block mt-1">
                          <a 
                            href={ensureCacheBuster(editingItem.file_url)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary"
                          >
                            <i className="bi bi-box-arrow-up-right me-1"></i>
                            Preview URL
                          </a>
                        </small>
                        {/* Hidden input to preserve file_url in form submission */}
                        <FormControl
                          type="hidden"
                          name="file_url"
                          value={editingItem.file_url}
                        />
                      </FormGroup>
                    )}
                  </Col>
                </Row>
                <div className="dev-form-actions">
                  <Button variant="secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading || uploadingImage}>
                    {loading || uploadingImage ? (uploadingImage ? 'Uploading image...' : 'Updating...') : 'Update'}
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>
      )}

      {/* Add/Edit Modal for Imported Sections */}
      {activeTab === 'imported-sections' && (
        <>
          <Modal show={showAddModal} onHide={() => { setShowAddModal(false); resetForm(); }} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>Add New Imported Section</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section ID *</FormLabel>
                      <FormControl
                        type="text"
                        name="section_id"
                        value={formData.section_id || ''}
                        onChange={handleFormChange}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Name *</FormLabel>
                      <FormControl
                        type="text"
                        name="section_name"
                        value={formData.section_name || ''}
                        onChange={handleFormChange}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Type</FormLabel>
                      <FormControl
                        as="select"
                        name="section_type"
                        value={formData.section_type || 'assets'}
                        onChange={handleFormChange}
                      >
                        <option value="assets">Assets</option>
                        <option value="extension">Extension</option>
                      </FormControl>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Rearrangement Type</FormLabel>
                      <FormControl
                        as="select"
                        name="rearrangement_type"
                        value={formData.rearrangement_type || 'dynamic'}
                        onChange={handleFormChange}
                      >
                        <option value="dynamic">Dynamic</option>
                        <option value="static">Static</option>
                      </FormControl>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Query Selector</FormLabel>
                      <FormControl
                        type="text"
                        name="section_query_selector"
                        value={formData.section_query_selector || ''}
                        onChange={handleFormChange}
                        placeholder="#content > div..."
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Original URL</FormLabel>
                      <FormControl
                        type="url"
                        name="section_original_url"
                        value={formData.section_original_url || ''}
                        onChange={handleFormChange}
                        placeholder="https://example.com/page"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup className="mb-3">
                  <FormLabel>HTML File</FormLabel>
                  <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                    <FormControl
                      type="file"
                      accept=".html,text/html"
                      onChange={handleHtmlFileChange}
                      style={{ flex: 1, minWidth: '140px' }}
                      disabled={!formData.section_id}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleValidateHtmlFile}
                      disabled={!selectedHtmlFile || validatingHtml || !formData.section_id}
                      title="Run checklist before upload"
                    >
                      {validatingHtml ? 'Validating...' : 'Validate / Analyze'}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={uploadHtmlToFirebase}
                      disabled={!selectedHtmlFile || uploadingHtml || !formData.section_id || !htmlAnalysisReport || (htmlAnalysisReport.errors && htmlAnalysisReport.errors.length > 0)}
                      title={!htmlAnalysisReport ? 'Select file and click Validate / Analyze first' : (htmlAnalysisReport.errors && htmlAnalysisReport.errors.length > 0) ? 'Fix errors and validate again' : 'Upload HTML'}
                    >
                      {uploadingHtml ? 'Uploading...' : 'Upload HTML'}
                    </Button>
                  </div>
                  {selectedHtmlFile && (
                    <small className="text-muted d-block mt-1">
                      Selected: {selectedHtmlFile.name}
                    </small>
                  )}
                  {htmlAnalysisReport && htmlAnalysisReport.fileName === selectedHtmlFile?.name && (
                    <div className="mt-2 p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <strong className="d-block mb-2">Checklist report</strong>
                      {htmlAnalysisReport.errors && htmlAnalysisReport.errors.length > 0 && (
                        <div className="mb-2">
                          <span className="text-danger fw-bold">Errors ({htmlAnalysisReport.errors.length}) — fix and validate again:</span>
                          <ul className="mb-0 mt-1 small text-danger">
                            {htmlAnalysisReport.errors.map((item, i) => (
                              <li key={i}>{item.message}{item.detail ? ` — ${item.detail}` : ''}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {htmlAnalysisReport.warnings && htmlAnalysisReport.warnings.length > 0 && (
                        <div className="mb-2">
                          <span className="text-warning fw-bold">Warnings ({htmlAnalysisReport.warnings.length}):</span>
                          <ul className="mb-0 mt-1 small text-warning">
                            {htmlAnalysisReport.warnings.map((item, i) => (
                              <li key={i}>{item.message}{item.detail ? ` — ${item.detail}` : ''}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(!htmlAnalysisReport.errors || htmlAnalysisReport.errors.length === 0) && (!htmlAnalysisReport.warnings || htmlAnalysisReport.warnings.length === 0) && (
                        <span className="text-success small">No issues found. You can upload.</span>
                      )}
                      {htmlAnalysisReport.errors && htmlAnalysisReport.errors.length === 0 && htmlAnalysisReport.warnings && htmlAnalysisReport.warnings.length > 0 && (
                        <span className="text-success small d-block mt-1">No errors. You can upload (warnings are optional to fix).</span>
                      )}
                    </div>
                  )}
                </FormGroup>
                <FormGroup className="mb-3">
                  <FormLabel>File URL (Version 1)</FormLabel>
                  <FormControl
                    type="url"
                    name="file_url"
                    value={formData.file_url || ''}
                    onChange={handleFormChange}
                    placeholder="Upload HTML file above or enter URL manually"
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                  {formData.file_url && (
                    <small className="text-muted d-block mt-1">
                      <a href={ensureCacheBuster(formData.file_url)} target="_blank" rel="noopener noreferrer" className="dev-link">
                        View HTML (v1)
                      </a>
                    </small>
                  )}
                  <small className="text-info d-block mt-1">
                    New sections start with version 1. Upload HTML to set the file URL.
                  </small>
                </FormGroup>
                <div className="dev-form-actions">
                  <Button variant="secondary" onClick={() => { setShowAddModal(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading || uploadingHtml}>
                    {loading || uploadingHtml ? (uploadingHtml ? 'Uploading HTML...' : 'Saving...') : 'Save'}
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>

          <Modal show={showEditModal} onHide={() => { 
            setShowEditModal(false); 
            resetForm(); 
            setHtmlUploadedSuccessfully(false);
            setSelectedVersionId(null);
          }} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>Edit Imported Section</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section ID *</FormLabel>
                      <FormControl
                        type="text"
                        name="section_id"
                        value={formData.section_id || ''}
                        onChange={handleFormChange}
                        required
                        readOnly={true}
                        disabled={true}
                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Name *</FormLabel>
                      <FormControl
                        type="text"
                        name="section_name"
                        value={formData.section_name || ''}
                        onChange={handleFormChange}
                        required
                        // disabled={true}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {editingItem && editingItem.versions && editingItem.versions.length > 0 && (
                  <Row>
                    <Col md={12}>
                      <FormGroup className="mb-3">
                        <FormLabel>Select Version *</FormLabel>
                        <div className="d-flex gap-2 align-items-start">
                          <div style={{ flex: 1 }}>
                            <FormControl
                              as="select"
                              value={selectedVersionId || ''}
                              onChange={handleVersionChange}
                              required
                            >
                              <option value="">-- Select Version --</option>
                              {editingItem.versions.map((version) => (
                                <option key={version.section_version_id} value={version.section_version_id}>
                                  {version.section_version_id} (Version {version.version}){version.version === editingItem.live_version ? ' - Live' : ''}
                                </option>
                              ))}
                            </FormControl>
                            <small className="text-muted d-block mt-1">
                              Select the version you want to edit. HTML upload will update the selected version.
                            </small>
                          </div>
                          <Button
                            variant="success"
                            onClick={handleSetLiveVersion}
                            disabled={!selectedVersionId || loading || (editingItem.versions?.find(v => v.section_version_id === selectedVersionId)?.version === editingItem.live_version)}
                            title={!selectedVersionId ? 'Please select a version first' : (editingItem.versions?.find(v => v.section_version_id === selectedVersionId)?.version === editingItem.live_version ? 'This version is already live' : 'Set selected version as live')}
                          >
                            {loading ? 'Setting...' : 'Set Live'}
                          </Button>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Type</FormLabel>
                      <FormControl
                        as="select"
                        name="section_type"
                        value={formData.section_type || 'assets'}
                        onChange={handleFormChange}
                        disabled={true}
                      >
                        <option value="assets">Assets</option>
                        <option value="extension">Extension</option>
                      </FormControl>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Rearrangement Type</FormLabel>
                      <FormControl
                        as="select"
                        name="rearrangement_type"
                        value={formData.rearrangement_type || 'dynamic'}
                        onChange={handleFormChange}
                        disabled={true}
                      >
                        <option value="dynamic">Dynamic</option>
                        <option value="static">Static</option>
                      </FormControl>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Section Query Selector</FormLabel>
                      <FormControl
                        type="text"
                        name="section_query_selector"
                        value={formData.section_query_selector || ''}
                        onChange={handleFormChange}
                        placeholder="#content > div..."
                        disabled={true}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <FormLabel>Original URL</FormLabel>
                      <FormControl
                        type="url"
                        name="section_original_url"
                        value={formData.section_original_url || ''}
                        onChange={handleFormChange}
                        placeholder="https://example.com/page"
                        disabled={true}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup className="mb-3">
                  <FormLabel>HTML File</FormLabel>
                  <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                    <FormControl
                      type="file"
                      accept=".html,text/html"
                      onChange={handleHtmlFileChange}
                      style={{ flex: 1, minWidth: '140px' }}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleValidateHtmlFile}
                      disabled={!selectedHtmlFile || validatingHtml}
                      title="Run checklist before upload"
                    >
                      {validatingHtml ? 'Validating...' : 'Validate / Analyze'}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={uploadHtmlToFirebase}
                      disabled={!selectedHtmlFile || uploadingHtml || !selectedVersionId || !htmlAnalysisReport || (htmlAnalysisReport.errors && htmlAnalysisReport.errors.length > 0)}
                      title={
                        !selectedVersionId
                          ? 'Please select a version first'
                          : (htmlAnalysisReport?.errors && htmlAnalysisReport.errors.length > 0)
                            ? 'Fix errors and validate again'
                            : !htmlAnalysisReport
                              ? 'Select file and click Validate / Analyze first'
                              : editingItem?.versions?.find(v => v.section_version_id === selectedVersionId)
                                ? `Will upload to version ${editingItem.versions.find(v => v.section_version_id === selectedVersionId).version} (${selectedVersionId})`
                                : 'Ready to upload'
                      }
                    >
                      {uploadingHtml ? 'Uploading...' : 'Upload HTML'}
                    </Button>
                  </div>
                  {selectedHtmlFile && (
                    <small className="text-muted d-block mt-1">
                      Selected: {selectedHtmlFile.name}
                    </small>
                  )}
                  {htmlAnalysisReport && htmlAnalysisReport.fileName === selectedHtmlFile?.name && (
                    <div className="mt-2 p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <strong className="d-block mb-2">Checklist report</strong>
                      {htmlAnalysisReport.errors && htmlAnalysisReport.errors.length > 0 && (
                        <div className="mb-2">
                          <span className="text-danger fw-bold">Errors ({htmlAnalysisReport.errors.length}) — fix and validate again:</span>
                          <ul className="mb-0 mt-1 small text-danger">
                            {htmlAnalysisReport.errors.map((item, i) => (
                              <li key={i}>{item.message}{item.detail ? ` — ${item.detail}` : ''}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {htmlAnalysisReport.warnings && htmlAnalysisReport.warnings.length > 0 && (
                        <div className="mb-2">
                          <span className="text-warning fw-bold">Warnings ({htmlAnalysisReport.warnings.length}):</span>
                          <ul className="mb-0 mt-1 small text-warning">
                            {htmlAnalysisReport.warnings.map((item, i) => (
                              <li key={i}>{item.message}{item.detail ? ` — ${item.detail}` : ''}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(!htmlAnalysisReport.errors || htmlAnalysisReport.errors.length === 0) && (!htmlAnalysisReport.warnings || htmlAnalysisReport.warnings.length === 0) && (
                        <span className="text-success small">No issues found. You can upload.</span>
                      )}
                      {htmlAnalysisReport.errors && htmlAnalysisReport.errors.length === 0 && htmlAnalysisReport.warnings && htmlAnalysisReport.warnings.length > 0 && (
                        <span className="text-success small d-block mt-1">No errors. You can upload (warnings are optional to fix).</span>
                      )}
                    </div>
                  )}
                  {htmlUploadedSuccessfully && editingItem && (
                    <small className="text-success d-block mt-2" style={{ fontWeight: 'bold' }}>
                      ✅ HTML uploaded successfully! {selectedVersionId ? `Selected version (${editingItem.versions?.find(v => v.section_version_id === selectedVersionId)?.section_version_id || 'N/A'})` : `Live version ${editingItem.live_version}`} has been updated.
                    </small>
                  )}
                  {!selectedVersionId && editingItem && (
                    <small className="text-warning d-block mt-2" style={{ fontWeight: 'bold' }}>
                      ⚠️ Please select a version above before uploading HTML file.
                    </small>
                  )}
                </FormGroup>
                <FormGroup className="mb-3">
                  <FormLabel>File URL {selectedVersionId ? `(Selected Version)` : `(Live Version)`}</FormLabel>
                  {/* <FormControl
                    type="url"
                    name="file_url"
                    value={formData.file_url || ''}
                    onChange={handleFormChange}
                    placeholder="Upload HTML file above or enter URL manually"
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  /> */}
                  {formData.file_url && (
                    <small className="text-muted d-block mt-1">
                      <a href={ensureCacheBuster(formData.file_url)} target="_blank" rel="noopener noreferrer" className="dev-link">
                        View Current HTML {selectedVersionId 
                          ? `(v${editingItem?.versions?.find(v => v.section_version_id === selectedVersionId)?.version || 'N/A'})`
                          : `(v${editingItem?.live_version || 1})`}
                      </a>
                      {selectedVersionId && (
                        <span className="text-info ms-2">
                          (Selected: {editingItem?.versions?.find(v => v.section_version_id === selectedVersionId)?.section_version_id || 'N/A'})
                        </span>
                      )}
                    </small>
                  )}
                  {/* {editingItem && !htmlUploadedSuccessfully && selectedVersionId && (
                    <small className="text-info d-block mt-1">
                      Selected Version: {editingItem.versions?.find(v => v.section_version_id === selectedVersionId)?.section_version_id || 'N/A'} (v{editingItem.versions?.find(v => v.section_version_id === selectedVersionId)?.version || 'N/A'}) - Upload HTML to update this version's file URL (will update existing version, not create new)
                    </small>
                  )} */}
                  {editingItem && !htmlUploadedSuccessfully && !selectedVersionId && (
                    <small className="text-info d-block mt-1">
                      Live Version: v{editingItem.live_version} - Select a version above and upload HTML to update that version's file URL
                    </small>
                  )}
                  {editingItem && htmlUploadedSuccessfully && (
                    <small className="text-success d-block mt-1" style={{ fontWeight: 'bold' }}>
                      ✅ {selectedVersionId ? 'Selected version' : `Live version ${editingItem.live_version}`} file URL has been updated successfully!
                    </small>
                  )}
                </FormGroup>
                <div className="dev-form-actions">
                  <Button variant="secondary" onClick={() => { 
                    setShowEditModal(false); 
                    resetForm(); 
                    setHtmlUploadedSuccessfully(false);
                    setSelectedVersionId(null);
                  }}>
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={loading || uploadingHtml}
                    title={htmlUploadedSuccessfully ? 'HTML uploaded successfully. Click Update to save all changes (query selector, base URL, and file URL).' : ''}
                  >
                    {loading || uploadingHtml 
                      ? (uploadingHtml ? 'Uploading HTML...' : 'Updating...') 
                      : 'Update'}
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>
        </>
      )}

    </div>
  );
};

export default DevDashboard;
